import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { DatePicker, TimePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { NumericTextBox, RadioButton, RadioButtonChangeEvent, RangeSlider, Switch } from "@progress/kendo-react-inputs";
import { useLocalization } from "@progress/kendo-react-intl";
import { Avatar } from "@progress/kendo-react-layout";
import { arrowRotateCwIcon, chartScatterSmoothLinesMarkersIcon, eyeIcon, eyeSlashIcon } from "@progress/kendo-svg-icons";
import axios from "axios";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { TransformComponent, TransformWrapper, useControls } from "react-zoom-pan-pinch";
import { AccessToken } from "../../../config/AccessToken";
import { getLikelihoodColor } from "../../../config/likelihoodColors";
import { loadingPanel } from "../../LoadingPanel";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { currentLocation } from "../../../assets/current-location";
import React from "react";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Card, CardHeader, CardBody } from "@progress/kendo-react-layout";
import { Chart, ChartTitle, ChartLegend, ChartSeries, ChartTooltip, ChartSeriesItem } from "@progress/kendo-react-charts";
import { AzureMapSpaghettiChart } from "./AzureMapSpaghettiChart";
import { fetchServerSettings } from "../../../config/serverSettings";
import { analysisCanvasPanelStyle, analysisControlPanelStyle, analysisLayoutStyle, analysisPanelCss } from "./analysisPanelStyles";


// Componente memoizzato per il grafico per evitare re-render inutili
const AccuracyChart = React.memo(({ data, localizationService }: { data: any[], localizationService: any }) => {
    const totalPoints = data.reduce((sum, item) => sum + item.value, 0);

    const tooltipRender = (context: any) => {
        const { category, color } = context?.dataItem ?? context?.point?.dataItem;

        return (
            <div style={{
                padding: '10px',
                background: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                minWidth: '200px'
            }}>
                <div style={{
                    fontSize: '13px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    paddingBottom: '6px',
                    borderBottom: '1px solid #e0e0e0',
                    color: 'black'
                }}>
                    {localizationService.toLanguageString('configuration.analysis.path_history.accuracy_distribution', 'NOT_FOUND')}
                </div>
                {data.map((item, index) => {
                    const percentage = totalPoints > 0 ? ((item.value / totalPoints) * 100).toFixed(1) : 0;
                    const isCurrentCategory = item.category === category;

                    return (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '4px',
                            padding: '3px',
                            background: isCurrentCategory ? '#f0f0f0' : 'transparent',
                            borderRadius: '3px',
                            fontWeight: isCurrentCategory ? 'bold' : 'normal'
                        }}>
                            <div style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: item.color,
                                marginRight: '8px',
                                borderRadius: '2px',
                                flexShrink: 0
                            }}></div>
                            <div style={{
                                fontSize: '12px',
                                color: 'black',
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '8px'
                            }}>
                                <span>{item.category}:</span>
                                <span><strong>{item.value}</strong> ({percentage}%)</span>
                            </div>
                        </div>
                    );
                })}
                {totalPoints > 0 && (
                    <div style={{
                        marginTop: '8px',
                        paddingTop: '6px',
                        borderTop: '1px solid #e0e0e0',
                        fontSize: '12px',
                        color: 'black',
                        fontWeight: 'bold'
                    }}>
                        {localizationService.toLanguageString('configuration.analysis.path_history.total', 'NOT_FOUND')}: {totalPoints} {localizationService.toLanguageString('configuration.analysis.path_history.points', 'NOT_FOUND')}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Chart onSeriesClick={(e) => { }} style={{ width: '250px', height: '150px', margin: '0 auto' }}>
            <ChartTitle visible={false} />
            <ChartLegend position="right" offsetX={-15} />
            <ChartTooltip render={tooltipRender} />
            <ChartSeries>
                <ChartSeriesItem
                    type="pie"
                    data={data}
                    categoryField="category"
                    field="value"
                    colorField="color"
                />
            </ChartSeries>
        </Chart>
    );
});

export const SpaghettiChart = () => {

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const localizationService = useLocalization();
    const [instance, accessTokenRequest] = AccessToken();
    let languageId: any = localStorage.getItem('languageId');

    const [loading, setLoading] = useState(false);

    const [devicesList, setDevicesList] = useState<any>([]);
    const [selectedDevices, setSelectedDevices] = useState<string[]>([]);

    const [checkedOnlyBound, setCheckedOnlyBound] = useState(false);
    const [checked, setChecked] = useState(false);
    const [isAdvanced, setIsAdvanced] = useState(false);
    const [analyzeEnabled, setAnalyzeEnabled] = useState(false);
    const [distance, setDistance] = useState('');
    const [distanceByDevice, setDistanceByDevice] = useState<Record<string, string>>({});

    const [dateTimeState, setDateTimeState] = useState<any>({
        fromDate: new Date(),
        toDate: new Date(),
        fromHour: new Date(),
        toHour: new Date()
    });

    const [intervalTimeValue, setIntervalTimeValue] = useState<any>(null);
    const [intervalTimeName, setIntervalTimeName] = useState<any>('');
    const [formattedIntervalDate, setFormattedIntervalDate] = useState<any>(null);
    const [spaghettiChartMessage, setSpaghettiChartMessage] = useState<any>(null);

    const [infoDevice, setInfoDevice] = useState<any>([])
    const [toDateLabel, setToDateLabel] = useState<any>('')
    const [progressiveDateLabel, setProgressiveDateLabel] = useState<any>('')
    const [progressiveFromDateLabel, setProgressiveFromDateLabel] = useState<any>('')

    const intervalTimeList = {
        "data": [
            {
                "id": -1,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.custom', 'NOT_FOUND'),
                "value": -1
            },
            {
                "id": 0,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.last_1_hour', 'NOT_FOUND'),
                "value": 60
            },
            {
                "id": 1,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.last_3_hours', 'NOT_FOUND'),
                "value": 180
            },
            {
                "id": 2,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.last_24_hours', 'NOT_FOUND'),
                "value": 1440
            },
            {
                "id": 3,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.today', 'NOT_FOUND'),
                "value": 0
            },
            {
                "id": 4,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.from_yesterday', 'NOT_FOUND'),
                "value": 1
            },
            {
                "id": 5,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.last_3_days', 'NOT_FOUND'),
                "value": 4320
            },
        ]
    }

    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [warningMessage, setWarningMessage] = useState(false);


    const [mapGeoreferenced, setMapGeoreferenced] = useState(false);
    const [listMaps, setListMaps] = useState([]);
    const [listMapNames, setListMapNames] = useState([]);
    const [mapName, setMapName] = useState([]);
    const [selectedMap, setSelectedMap] = useState<any>([]);
    const [showZone, setShowZone] = useState<any>(false);
    const [showZoneMapGeo, setShowZoneMapGeo] = useState<any>([]);
    const [showInfoDevice, setShowInfoDevice] = useState<Record<string, boolean>>({});
    const [hiddenDevices, setHiddenDevices] = useState<Record<string, boolean>>({});
    const [analysisRunId, setAnalysisRunId] = useState(0);
    const [showAllPoints, setShowAllPoints] = useState<any>(false);
    const [showColor, setShowColor] = useState<any>(false);
    const [signalsGrouping, setSignalsGrouping] = useState<any>(false);
    const [averagePositions, setAveragePositions] = useState<any>(false);
    const [groupingType, setGroupingType] = useState<'near' | 'each'>('near');
    const [groupingValues, setGroupingValues] = useState({ near: 0, each: 0 });
    const [isValid, setIsValid] = useState<any>(false);

    const [toggleGeoStatic, setToggleGeoStatic] = useState<any>(true);



    const mapRef: any = useRef(null);
    const mapCanvasRef: any = useRef(null)
    const zoneCanvasRef: any = useRef(null);
    const lineCanvasRef: any = useRef(null);
    const markerCanvasRef: any = useRef(null);
    const sliderFrameRef = useRef<number | null>(null);
    const sliderMarkerHoverDataRef = useRef<any[]>([]);
    const markerCanvasSizeRef = useRef<{ width: number; height: number; ratio: number }>({ width: 0, height: 0, ratio: 1 });
    const lastSliderIndexRef = useRef<string>('');

    type Coordinate = [number, number]; // una coppia [lat, lng]
    type CoordinateGroup = { coord: Coordinate[]; deviceMac?: string };
    const [coordinates, setCoordinates] = useState<CoordinateGroup[]>([]);

    const staticPathPalette = useMemo(() => [
        "#E11D48",
        "#0EA5E9",
        "#16A34A",
        "#F59E0B",
        "#7C3AED",
        "#14B8A6"
    ], []);

    const getDevicePathColor = useCallback((deviceMac?: string, fallbackIndex: number = 0) => {
        const selectedIndex = deviceMac ? selectedDevices.indexOf(deviceMac) : -1;
        const colorIndex = selectedIndex >= 0 ? selectedIndex : fallbackIndex;
        return staticPathPalette[colorIndex % staticPathPalette.length];
    }, [selectedDevices, staticPathPalette]);

    const staticPathSeries = useMemo(() => {
        return (coordinates || [])
            .map((group: any, index: number) => {
                const points = Array.isArray(group?.coord?.[0]) ? group.coord[0] : [];
                if (!points.length) {
                    return null;
                }

                const deviceMac = group?.deviceMac || selectedDevices[index] || `device-${index + 1}`;

                return {
                    deviceMac,
                    points,
                    color: getDevicePathColor(deviceMac, index),
                };
            })
            .filter((series: any) => Boolean(series));
    }, [coordinates, selectedDevices, getDevicePathColor]);

    const visibleStaticPathSeries = useMemo(
        () => staticPathSeries.filter((series: any) => !hiddenDevices[series.deviceMac]),
        [staticPathSeries, hiddenDevices]
    );

    const visibleCoordinates = useMemo(
        () => (coordinates || []).filter((group: any) => !group?.deviceMac || !hiddenDevices[group.deviceMac]),
        [coordinates, hiddenDevices]
    );

    const visibleSelectedDevices = useMemo(
        () => selectedDevices.filter((deviceMac: string) => !hiddenDevices[deviceMac]),
        [selectedDevices, hiddenDevices]
    );

    const signalsByDevice = useMemo(() => {
        return staticPathSeries.reduce((acc: Record<string, number>, series: any) => {
            const points = Array.isArray(series?.points) ? series.points : [];
            acc[series.deviceMac] = points.length;
            return acc;
        }, {});
    }, [staticPathSeries]);

    const [renderCoordinates, setRenderCoordinates] = useState<any[]>([]);

    const [sliderRangePercent, setSliderRangePercent] = useState<{ start: number; end: number }>({ start: 0, end: 100 });
    const timeSliderMin = 0;
    const timeSliderMax = 100;

    const [countWakeups, setCountWakeups] = useState<number>(0);

    const [noData, setNoData] = useState(true);
    const [messageNoData, setMessageNoData] = useState(false);
    const [messageTooData, setMessageTooData] = useState(false);
    const [messageMaxDevices, setMessageMaxDevices] = useState(false);
    const [isDeviceMultiSelectOpen, setIsDeviceMultiSelectOpen] = useState(false);
    const maxDevicesMessageTimerRef = useRef<number | null>(null);

    const [mapImageWidth, setMapImageWidth] = useState<any>(0);
    const [mapImageHeight, setMapImageHeight] = useState<any>(0);

    const [counterLimits, setCounterLimits] = useState({
        maxPoints: 32000,
        maxDevices: 3,
    });

    const COUNTER_MAX_POINTS: number = counterLimits.maxPoints;
    const COUNTER_MAX_DEVICES: number = counterLimits.maxDevices;
    const maxDevicesMessage = localizationService
        .toLanguageString('configuration.analysis.path_history.max_devices_warning', 'You can select up to #sym:maxDevices devices - current max device number is set to #sym:maxDevices')
        .replace(/#sym:maxDevices/g, String(COUNTER_MAX_DEVICES));

    const showMaxDevicesWarning = () => {
        setIsDeviceMultiSelectOpen(false);
        setMessageMaxDevices(true);

        if (maxDevicesMessageTimerRef.current !== null) {
            window.clearTimeout(maxDevicesMessageTimerRef.current);
        }

        maxDevicesMessageTimerRef.current = window.setTimeout(() => {
            setMessageMaxDevices(false);
            maxDevicesMessageTimerRef.current = null;
        }, 5000);
    };

    const parsePositiveInteger = (value: any, fallback: number) => {
        const parsedValue = Number(value);
        return Number.isFinite(parsedValue) && parsedValue > 0
            ? Math.floor(parsedValue)
            : fallback;
    };

    const getCounterLimits = async () => {
        const applyServerSettings = (settings: any[]) => {
            const maxDevicesSetting = settings?.find((setting: any) => setting.id == 'HeatmapSearch_MaxNumberDevices')?.value;
            const maxPointsSetting = settings?.find((setting: any) => setting.id == 'HeatmapSearch_MaxNumberResults')?.value;

            setCounterLimits((prev) => ({
                maxPoints: parsePositiveInteger(maxPointsSetting, prev.maxPoints),
                maxDevices: parsePositiveInteger(maxDevicesSetting, prev.maxDevices),
            }));
        };

        const commonOperations = async (accessToken: any) => {
            try {
                const settings = await fetchServerSettings(SERVER_URL, accessToken, languageId);
                applyServerSettings(settings ?? []);
            } catch (error: any) {
                console.log(error);
            }
        };

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest)
                .then((accessTokenResponse: any) => {
                    let accessToken = accessTokenResponse.accessToken;
                    commonOperations(accessToken);
                })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    };

    const getDeviceList = async (flag: boolean) => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            await axios.get(`${SERVER_URL}/device/active`, {
                headers,
                params: {
                    enabled: flag
                }
            })
                .then((res) => {
                    const data: any = res?.data?.data

                    if (flag) {
                        setDevicesList(data);
                        setDeviceListSkip(0);
                    }
                    setLoading(false);
                }).catch((data) => {
                    setError(data?.message);
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    //  call API on page load
    useEffect(() => {
        getDeviceList(true)
        getMapList();
        getCounterLimits();
    }, [])

    const applyDeviceSelection = (nextMacs: string[]) => {
        setMapName([]);
        setAnalyzeEnabled(false);
        setIsValid(false);
        setSelectedDevices(nextMacs);

        if (nextMacs.length === 0) {
            setChecked(false);
            setIsAdvanced(false);
            setIsDeviceMultiSelectOpen(false);
            setIntervalTimeValue(null);
            setIntervalTimeName(null);
        }
    };

    useEffect(() => {
        setHiddenDevices((prev) => {
            const next: Record<string, boolean> = {};
            selectedDevices.forEach((mac) => {
                if (prev[mac]) {
                    next[mac] = true;
                }
            });
            return next;
        });
    }, [selectedDevices]);

    const convertToSystemLocal = (date: Date): string => {
        const utc = date.getTime()

        const localDate = new Date(utc);

        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(localDate.getDate())}/${pad(localDate.getMonth() + 1)}/${localDate.getFullYear()}, ${pad(localDate.getHours())}:${pad(localDate.getMinutes())}:${pad(localDate.getSeconds())}`;
    }

    const onChangeIntervalTime = (ev: any) => {
        if (!ev?.value) {
            return;
        }

        const now = new Date();

        const midnightToday = new Date();
        midnightToday.setHours(0, 0, 0, 0);

        //const midnightYesterday = new Date(midnightToday.getTime() - 24 * 60 * 60 * 1000);
        const midnightYesterday = new Date(midnightToday);
        midnightYesterday.setDate(midnightYesterday.getDate() - 1);

        const midnightThreeDaysAgo = new Date(midnightToday);
        midnightThreeDaysAgo.setDate(midnightThreeDaysAgo.getDate() - 3);

        let timeAgo: Date = now;

        switch (ev.value.id) {
            case -1: // custom range
                setIsAdvanced(true);
                setIntervalTimeName(ev.value);
                setChecked(true);
                return;

            case 0: // last 1 hour
            case 1: // last 3 hours
            case 2: // last 24 hours
                timeAgo = new Date(now.getTime() - ev.value.value * 60 * 1000);
                setIsAdvanced(false);
                break;

            case 3: // today
                timeAgo = midnightToday;
                setIsAdvanced(false);
                break;

            case 4: // from yesterday
                timeAgo = midnightYesterday;
                setIsAdvanced(false);
                break;

            case 5: // last 3 days
                timeAgo = midnightThreeDaysAgo;
                setIsAdvanced(false);
                break;

            default:
                return;
        }


        const formattedIntervalDate = convertToSystemLocal(timeAgo);

        const startDate = timeAgo.toISOString();

        setIntervalTimeValue(startDate);
        setIntervalTimeName(ev.value);
        setFormattedIntervalDate(formattedIntervalDate);

        setChecked(true);
    }

    const switchOnlyBound = (onlyBound: boolean) => {
        setCheckedOnlyBound(onlyBound);
        setDeviceListSkip(0);

        if (!onlyBound) {
            return;
        }

        const boundMacs = new Set(devicesList.filter((d: any) => d?.name != null).map((d: any) => d.mac));
        const nextMacs = selectedDevices.filter((mac) => boundMacs.has(mac));
        if (nextMacs.length !== selectedDevices.length) {
            applyDeviceSelection(nextMacs);
        }
    };

    const mergeDateAndTime = (date: Date, time: Date) => {
        const merged = new Date(date);
        merged.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
        return merged;
    };

    const selectedRangeDates = useMemo(() => {
        const now = new Date();

        if (checked && isAdvanced) {
            const fromDateValue = mergeDateAndTime(
                dateTimeState?.fromDate ?? now,
                dateTimeState?.fromHour ?? now
            );
            const toDateValue = mergeDateAndTime(
                dateTimeState?.toDate ?? now,
                dateTimeState?.toHour ?? now
            );

            if (fromDateValue.getTime() <= toDateValue.getTime()) {
                return { from: fromDateValue, to: toDateValue };
            }

            return { from: toDateValue, to: fromDateValue };
        }

        const parsedInterval = intervalTimeValue ? new Date(intervalTimeValue) : null;
        const fromDateValue = parsedInterval && !isNaN(parsedInterval.getTime())
            ? parsedInterval
            : now;

        return { from: fromDateValue, to: now };
    }, [checked, isAdvanced, dateTimeState, intervalTimeValue]);

    const sliderRangeLabels = useMemo(() => {
        if (checked && isAdvanced) {
            return {
                from: convertToSystemLocal(selectedRangeDates.from),
                to: convertToSystemLocal(selectedRangeDates.to)
            };
        }

        return {
            from: formattedIntervalDate || convertToSystemLocal(selectedRangeDates.from),
            to: convertToSystemLocal(selectedRangeDates.to)
        };
    }, [checked, isAdvanced, formattedIntervalDate, selectedRangeDates]);

    const spaghettiProgressiveFrom = useMemo(() => {
        return progressiveFromDateLabel || sliderRangeLabels.from || '';
    }, [progressiveFromDateLabel, sliderRangeLabels.from]);

    const spaghettiProgressiveTo = useMemo(() => {
        return progressiveDateLabel || sliderRangeLabels.to || '';
    }, [progressiveDateLabel, sliderRangeLabels.to]);

    const isValidYear = (date: Date) => {
        const year = date.getFullYear();
        return year >= 1000; // blocca 0000, 0001, ecc.
    };

    const handleChangeDate = (e: any) => {
        const { name, value } = e.target;

        if (value && !isValidYear(value)) {
            return; // ignora input
        }

        setDateTimeState((prev: any) => {
            const updated = { ...prev, [name]: value };

            if (name === "toDate" && value && prev.fromDate && value < prev.fromDate) {
                return prev; // ignora update
            }

            if (name === "fromDate" && value) {
                updated.fromHour = mergeDateAndTime(value, prev.fromHour);
            }

            if (name === "toDate" && value) {
                updated.toHour = mergeDateAndTime(value, prev.toHour);
            }

            return updated;
        });

        setChecked(true);
    };

    const handleBlurDate = (e: any) => {
        const { name, value } = e.target;

        if (!value) return;

        setDateTimeState((prev: any) => {
            const updated = { ...prev };

            const newDate = e.target.value;

            if (name === "toDate" && prev.fromDate && newDate < prev.fromDate) {
                updated.toDate = null;
            }

            if (name === "fromDate" && prev.toDate && newDate > prev.toDate) {
                updated.toDate = null;
            }

            return updated;
        });
    };
    const handleBlurTime = (e: any) => {
        const { name, value } = e.target;

        if (!value) return;

        setDateTimeState((prev: any) => {
            const updated = { ...prev };

            const newDate = e.target.value;

            if (name === "toHour" && prev.fromHour && newDate < prev.fromHour) {
                updated.toHour = null;
            }

            if (name === "fromHour" && prev.toHour && newDate > prev.toHour) {
                updated.fromHour = null;
            }

            return updated;
        });
    };

    const handleChangeTime = (e: any) => {
        const { name, value } = e.target;

        setDateTimeState((prev: any) => {
            const updated = { ...prev, [name]: value };

            if (name === "toHour" && value && prev.fromHour && value < prev.fromHour) {
                return prev; // ignora update
            }

            if (name === "fromHour" && value) {
                updated.fromDate = mergeDateAndTime(prev.fromDate, value);
            }

            if (name === "toHour" && value) {
                updated.toDate = mergeDateAndTime(prev.toDate, value);
            }

            return updated;
        });

        setChecked(true);
    };

    const buildUtcRangeForRequests = () => {
        const nowUtc = new Date().toISOString();
        const useCustomRange = checked && isAdvanced;

        if (!useCustomRange) {
            return {
                startDate: intervalTimeValue,
                endDate: nowUtc
            };
        }

        const fromDate = dateTimeState?.fromDate ?? new Date();
        const toDate = dateTimeState?.toDate ?? new Date();
        const fromHour = dateTimeState?.fromHour ?? new Date();
        const toHour = dateTimeState?.toHour ?? new Date();

        const startDateUtc = mergeDateAndTime(fromDate, fromHour).toISOString();
        const endDateUtc = mergeDateAndTime(toDate, toHour).toISOString();

        return {
            startDate: startDateUtc,
            endDate: endDateUtc
        };
    };

    const clearRightPanel = () => {
        setCoordinates([]);
        setInfoDevice([]);
        setShowZone(false);
        setShowZoneMapGeo([]);
        setShowAllPoints(false);
        setHiddenDevices({});
        setShowInfoDevice({});
        setDistanceByDevice({});
        setNoData(true);

        const mapCtx = mapCanvasRef?.current?.getContext("2d");
        const zoneCtx = zoneCanvasRef?.current?.getContext("2d");
        const lineCtx = lineCanvasRef?.current?.getContext("2d");
        const markerCtx = markerCanvasRef?.current?.getContext("2d");

        if (mapCanvasRef?.current) {
            mapCtx?.clearRect(0, 0, mapCanvasRef.current.width, mapCanvasRef.current.height);
        }
        if (zoneCanvasRef?.current) {
            zoneCtx?.clearRect(0, 0, zoneCanvasRef.current.width, zoneCanvasRef.current.height);
        }
        if (lineCanvasRef?.current) {
            lineCtx?.clearRect(0, 0, lineCanvasRef.current.width, lineCanvasRef.current.height);
        }
        if (markerCanvasRef?.current) {
            markerCtx?.clearRect(0, 0, markerCanvasRef.current.width, markerCanvasRef.current.height);
        }
    };

    const isZeroCoordinate = (latitude: any, longitude: any) => {
        return Number(latitude) === 0 && Number(longitude) === 0;
    };

    const filterOutZeroCoordinatesWakeups = (wakeups: any[] = []) => {
        return wakeups.filter((wakeup: any) => {
            const latitude = wakeup?.position?.latitude;
            const longitude = wakeup?.position?.longitude;

            if (latitude === undefined || latitude === null || longitude === undefined || longitude === null) {
                return false;
            }

            return !isZeroCoordinate(latitude, longitude);
        });
    };

    const parseDistanceValue = (value: any): string => {
        if (value == null) {
            return '';
        }

        const normalizedValue = String(value).trim();
        return normalizedValue;
    };

    const escapeTooltipHtml = (value: any): string =>
        String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

    const buildPathHistoryTooltipHtml = (params: {
        title: string;
        accentColor?: string;
        rows: Array<{ label: string; value: string; mono?: boolean }>;
    }) => {
        const accentColor = params.accentColor || '#198692';
        const rowsHtml = params.rows
            .map((row) => `<div class="device-tooltip__row"><dt>${escapeTooltipHtml(row.label)}</dt><dd>${row.mono ? `<span class="device-tooltip__mono">${escapeTooltipHtml(row.value)}</span>` : escapeTooltipHtml(row.value)}</dd></div>`)
            .join('');

        return `<div class="device-tooltip" style="--device-tooltip-accent:${accentColor};">
            <div class="device-tooltip__header">
                <span class="device-tooltip__swatch" aria-hidden="true"></span>
                <span class="device-tooltip__title">${escapeTooltipHtml(params.title)}</span>
            </div>
            <dl class="device-tooltip__body">${rowsHtml}</dl>
        </div>`;
    };

    const retrieveWakeups = async (mapId?: any, isGrouped?: boolean) => {
        setLoading(true);
        setShowColor(false);

        const { startDate, endDate } = buildUtcRangeForRequests();
        setToDateLabel(sliderRangeLabels.to)
        setProgressiveDateLabel('')
        setProgressiveFromDateLabel('')

        const params: any = {
            startDate,
            endDate,
            macs: selectedDevices
        };

        if (signalsGrouping && isGrouped !== false) {

            if (groupingType === 'near') {
                params.groupNearDistance = groupingValues['near'];
            } else {
                params.groupBy = groupingValues['each'];
            }
            if (averagePositions) {
                params.averagePositions = averagePositions;
            }
        }

        if (mapId) {
            params.mapId = mapId;
        }


        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            axios.post(`${SERVER_URL}/wakeup/logs/spaghetti`, params, { headers })
                .then((response: any) => {

                    const rawWakeups = response?.data?.data?.wakeUpLogs ?? [];
                    const data = filterOutZeroCoordinatesWakeups(rawWakeups);
                    if (data?.length == 0) {
                        setAnalyzeEnabled(false);
                        setNoData(true);
                        setMessageNoData(true);

                        setTimeout(() => {
                            setMessageNoData(false);
                        }, 3000);

                        setLoading(false)
                        return
                    }

                    setCountWakeups(data?.length);
                    setAnalysisRunId((prev) => prev + 1);

                    const distance = parseDistanceValue(response?.data?.data?.distance)
                    setDistance(distance)

                    const deviceDistances = response?.data?.data?.deviceDistances ?? [];
                    const nextDistanceByDevice: Record<string, string> = {};
                    deviceDistances.forEach((item: any) => {
                        const deviceMac = typeof item?.deviceMac === 'string' ? item.deviceMac : '';
                        if (!deviceMac) {
                            return;
                        }

                        nextDistanceByDevice[deviceMac] = parseDistanceValue(item?.distance);
                    });
                    setDistanceByDevice(nextDistanceByDevice);

                    const mapId: any = response?.data?.data?.mapId

                    // Single response for all devices: split points by deviceMac to keep one track per device.
                    const coordinatesByMac: Record<string, any[]> = {};
                    data.forEach((coord: any) => {
                        const mac = coord.deviceMac;
                        if (!coordinatesByMac[mac]) {
                            coordinatesByMac[mac] = [];
                        }
                        coordinatesByMac[mac].push(
                            [coord.position.latitude, coord.position.longitude, coord.position.color, coord.datetime, coord.lastPositionDatetime, coord.likelihood, coord.localTime]
                        );
                    });

                    const orderedMacs = [
                        ...selectedDevices.filter((mac: any) => coordinatesByMac[mac]),
                        ...Object.keys(coordinatesByMac).filter((mac) => !selectedDevices.includes(mac))
                    ];

                    setCoordinates(orderedMacs.map((deviceMac: any) => ({ deviceMac, coord: [coordinatesByMac[deviceMac] as any] })));

                    if (!signalsGrouping) {
                        const allCoordinates = orderedMacs.flatMap((mac: any) => coordinatesByMac[mac]);
                        setInfoDevice(allCoordinates.map(([lat, lon, color, date, lastPositionDatetime, likelihood, localTime]: any) => [lon, lat, color, date, lastPositionDatetime, likelihood, localTime]))
                    }

                    getMapById(mapId);

                    setSpaghettiChartMessage(true);
                    setAnalyzeEnabled(true);
                    setNoData(false);
                    setShowZone(false)
                    setShowAllPoints(false)

                    setTimeout(() => {
                        setSpaghettiChartMessage(false);
                    }, 3000);

                    setShowZone(false)

                    setLoading(false)
                }).catch((data) => {
                    if (data?.response.data.errorMessage == 'Error executing the API: Start date must be before end date') {
                        setError(true)
                        if (languageId == 'it-IT') {
                            setErrorMessage("Errore: La data di inizio deve essere prima della data di fine");
                        } else {
                            setErrorMessage("Error: Start date must be before end date");
                        }
                    } else if (data?.response.data.errorMessage == 'Error executing the API: There are no wakeups available for the selected map') {
                        setWarningMessage(true)
                        if (languageId == 'it-IT') {
                            setErrorMessage("Non ci sono wakeup disponibili per la mappa selezionata");
                        } else {
                            setErrorMessage("There are no wakeups available for the selected map");
                        }
                    }
                    if (!error) {
                        setTimeout(() => {
                            setError(false);
                        }, 3000);
                    }
                    if (!warningMessage) {
                        setTimeout(() => {
                            setWarningMessage(false);
                        }, 3000);
                    }
                    clearRightPanel();
                    setLoading(false)
                })
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken)
            })
        }
    }

    const getMapById = async (mapId: any) => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/map/${mapId}`, { headers })
                .then((res) => {
                    const mapSelected = res?.data.data

                    if(mapSelected.georeferenced != 1) {
                        setMapGeoreferenced(false)
                    }else{
                        toggleGeoStatic ? setMapGeoreferenced(true) : setMapGeoreferenced(false)
                    }
                    

                    renderSelectedMap(mapSelected)

                    setSelectedMap(mapSelected)
                    setMapName(mapSelected.name)

                    const mapCtx = mapCanvasRef?.current?.getContext("2d");
                    const mapWidth = mapCanvasRef?.width ?? mapCanvasRef?.current?.width
                    const mapHeight = mapCanvasRef?.height ?? mapCanvasRef?.current?.height
                    mapCtx?.clearRect(0, 0, mapWidth, mapHeight)

                    setLoading(false);
                })
                .catch((error) => {
                    clearRightPanel();
                    setError(true);
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const projectCoordinateToCanvas = useCallback((coordinate: any, canvasWidth: number, canvasHeight: number) => {
        if (!coordinate || !canvasWidth || !canvasHeight) {
            return null;
        }

        const latitude = Number(coordinate[0]);
        const longitude = Number(coordinate[1]);

        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
            return null;
        }

        if (!toggleGeoStatic && selectedMap?.botLeft && selectedMap?.botRight && selectedMap?.topLeft) {
            const lonSpan = Number(selectedMap.botRight.longitude) - Number(selectedMap.botLeft.longitude);
            const latSpan = Number(selectedMap.topLeft.latitude) - Number(selectedMap.botLeft.latitude);

            if (lonSpan && latSpan) {
                const x = canvasWidth * (longitude - Number(selectedMap.botLeft.longitude)) / lonSpan;
                const y = canvasHeight * (Number(selectedMap.topLeft.latitude) - latitude) / latSpan;
                return { x, y };
            }
        }

        const x = canvasWidth * longitude;
        const y = canvasHeight - canvasHeight * latitude;
        return { x, y };
    }, [toggleGeoStatic, selectedMap]);

    const drawStaticPathSeries = useCallback((ctx: CanvasRenderingContext2D, canvasWidth: number, canvasHeight: number, progressStartRatio: number = 0, progressEndRatio: number = 1) => {
        if (!visibleStaticPathSeries.length) {
            return;
        }

        visibleStaticPathSeries.forEach((series: any) => {
            const points = series?.points || [];

            if (points.length < 2) {
                return;
            }

            const clampedStartRatio = Math.max(0, Math.min(1, progressStartRatio));
            const clampedEndRatio = Math.max(0, Math.min(1, progressEndRatio));
            const minIndex = Math.max(0, Math.min(points.length - 1, Math.round((points.length - 1) * Math.min(clampedStartRatio, clampedEndRatio))));
            const maxIndex = Math.max(0, Math.min(points.length - 1, Math.round((points.length - 1) * Math.max(clampedStartRatio, clampedEndRatio))));

            for (let i = Math.max(1, minIndex + 1); i <= maxIndex; i++) {
                const previousPoint = points[i - 1];
                const currentPoint = points[i];
                const from = projectCoordinateToCanvas(previousPoint, canvasWidth, canvasHeight);
                const to = projectCoordinateToCanvas(currentPoint, canvasWidth, canvasHeight);

                if (!from || !to) {
                    continue;
                }

                const segmentColor = showColor ? getLikelihoodColor(currentPoint?.[5]) : series.color;

                ctx.beginPath();
                ctx.moveTo(Math.round(from.x), Math.round(from.y));
                ctx.lineTo(Math.round(to.x), Math.round(to.y));
                ctx.strokeStyle = segmentColor;
                ctx.lineWidth = 2;
                ctx.stroke();
            }
        });
    }, [visibleStaticPathSeries, projectCoordinateToCanvas, showColor]);

    const drawAllMarkers = useCallback(() => {

        const markerCanvas = markerCanvasRef.current;

        if (!markerCanvas) return;

        const ctx = markerCanvas.getContext("2d");

        if (!ctx) return;

        if (!showAllPoints) {
            ctx.clearRect(0, 0, markerCanvas.width, markerCanvas.height);
            return;
        }

        if (!visibleStaticPathSeries.length || !mapImageWidth || !mapImageHeight) {
            return;
        }

        const ratio = window.devicePixelRatio || 1;

        const targetWidth = mapImageWidth * ratio;

        const targetHeight = mapImageHeight * ratio;

        if (markerCanvas.width !== targetWidth || markerCanvas.height !== targetHeight) {

            markerCanvas.width = targetWidth;
            markerCanvas.height = targetHeight;

            markerCanvas.style.width = `${mapImageWidth}px`;

            markerCanvas.style.height = `${mapImageHeight}px`;
        }

        ctx.setTransform(1, 0, 0, 1, 0, 0);

        ctx.clearRect(0, 0, markerCanvas.width, markerCanvas.height);
        ctx.scale(ratio, ratio);
        visibleStaticPathSeries.forEach((series: any) => {
            series.points?.forEach((coordinate: any) => {
                const projected = projectCoordinateToCanvas(coordinate, mapImageWidth, mapImageHeight);

                if (!projected) {
                    return;
                }

                const baseX = Math.round(projected.x) - 5;
                const baseY = Math.round(projected.y);

                ctx.fillStyle = showColor ? getLikelihoodColor(coordinate?.[5]) : series.color;
                ctx.lineWidth = 1;
                ctx.strokeStyle = "white";

                ctx.beginPath();
                ctx.arc(baseX, baseY, 8, Math.PI, 0, false);
                ctx.lineTo(baseX, baseY + 7 * 2.2);
                ctx.imageSmoothingEnabled = true;
                ctx.closePath();
                ctx.fill();
                ctx.stroke();
                ctx.save();
                ctx.globalCompositeOperation = "destination-out";

                ctx.beginPath();
                ctx.arc(baseX, baseY, 3, 0, 2 * Math.PI);
                ctx.fill();
                ctx.restore();
            });
        });

    }, [showAllPoints, visibleStaticPathSeries, mapImageWidth, mapImageHeight, projectCoordinateToCanvas, showColor]);

    const renderSelectedMap = (map: any) => {
        const mapCanvas = !mapCanvasRef.current ? document.getElementById('mapCanvas') : mapCanvasRef.current;
        const zoneCanvas = !zoneCanvasRef.current ? document.getElementById('zoneCanvas') : zoneCanvasRef.current;
        const lineCanvas = !lineCanvasRef.current ? document.getElementById('lineCanvas') : lineCanvasRef.current

        const image = new Image();
        image.src = `data:image/jpeg;base64, ${map.image}`;

        image.onload = () => {
            // Check if canvas elements exist before setting properties
            if (!mapCanvas || !zoneCanvas || !lineCanvas) {
                console.error('Canvas elements not found in DOM');
                return;
            }

            mapCanvas.width = zoneCanvas.width = lineCanvas.width = image?.naturalWidth;
            mapCanvas.height = zoneCanvas.height = lineCanvas.height = image?.naturalHeight;

            const mapCtx = mapCanvas.getContext('2d');
            mapCtx.drawImage(image, 0, 0);
            mapCtx.transform(1, 0, 0, -1, 0, mapCanvasRef.current.height)

            const lineCtx = lineCanvas?.getContext("2d");

            if (lineCtx) {
                lineCtx.clearRect(0, 0, lineCanvas.width, lineCanvas.height);
                drawStaticPathSeries(lineCtx, lineCanvas.width, lineCanvas.height, 1);
            }

            setMapImageWidth(mapCanvas.width)
            setMapImageHeight(mapCanvas.height)

            drawAllMarkers();

        };

        const markerCanvas = markerCanvasRef.current;
        if (!markerCanvas) return;
        const ctx = markerCanvas.getContext("2d");
        ctx.clearRect(0, 0, markerCanvas.width, markerCanvas.height);
    }

    const changeMap = (event: any) => {

        let currMap: any = ''
        if (event?.value != undefined) {
            currMap = event?.value ?? event ?? ''
        } else {
            currMap = event.name ?? event ?? ''
        }

        const currListMaps: any = listMaps
        const isMapGeo = currListMaps?.find((map: any) => map.name == currMap)?.georeferenced
        const booleanIsMapGeo: any = isMapGeo == 0 ? false : true
        localStorage.setItem('mapGeorefenced', booleanIsMapGeo)
        setMapGeoreferenced(booleanIsMapGeo)

        let mapSelected = currListMaps?.find((map: any) => map.name == currMap)
        const updateMapSelected = mapSelected ?? currListMaps?.find((map: any) => map.id == currMap)
        if (updateMapSelected == null) return

        checkResults(updateMapSelected.id);


        //setAnalyzeEnabled(true)
        setSelectedMap(updateMapSelected)
        setMapName(updateMapSelected.name)

        //setShowZone(false)
        //setIsValid(false)

        //getMapById(updateMapSelected?.id)
    }

    const getZoneList = async (mapId: number, isGeoreferenced?: boolean) => {

        if (mapId == null || mapId == undefined) return

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/zone/bymapid/${mapId}?isGeoreferenced=${isGeoreferenced}`, { headers })
                .then((res) => {
                    const listZones = res?.data?.data

                    let zoneCtx: any;
                    if (zoneCanvasRef.current != null) {
                        zoneCtx = zoneCanvasRef?.current?.getContext("2d");
                        zoneCtx.clearRect(0, 0, zoneCanvasRef.width, zoneCanvasRef.height);
                    }

                    listZones?.forEach((zone: any) => {
                        if (!isGeoreferenced) {
                            zoneCtx.beginPath();
                            zoneCtx.fillStyle = zone.color
                            zoneCtx.moveTo(zoneCanvasRef?.current.width * zone.points[0].longitude, zoneCanvasRef?.current.height - zoneCanvasRef?.current.height * zone.points[0].latitude);
                            zone.points.forEach((points: any) => {
                                zoneCtx.lineTo(zoneCanvasRef?.current.width * points.longitude, zoneCanvasRef?.current.height - (zoneCanvasRef?.current.height * points.latitude));
                            });
                            zoneCtx.globalAlpha = 0.4;
                            zoneCtx.fill();
                        } else {
                            setShowZoneMapGeo(listZones)
                        }
                    });
                    setLoading(false);
                })
                .catch((error) => {
                    setLoading(false);
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                });
        }
    }

    const Controls = () => {
        const { zoomIn, zoomOut, resetTransform } = useControls();

        return (
            <div className="k-button" style={{ border: 'none', marginTop: '10px' }}>
                <button
                    style={{
                        position: 'absolute',
                        width: '32px',
                        height: '32px',
                        zIndex: 10,
                        top: '0px',
                        left: '10px',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        cursor: 'pointer'
                    }}
                    title={localizationService.toLanguageString('map.zoom_in', 'NOT_FOUND')}
                    onClick={() => zoomIn()}>+</button>
                <button
                    style={{
                        position: 'absolute',
                        width: '32px',
                        height: '32px',
                        zIndex: 10,
                        top: '0px',
                        left: '47px',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        cursor: 'pointer'
                    }}
                    title={localizationService.toLanguageString('map.zoom_out', 'NOT_FOUND')}
                    onClick={() => zoomOut()}>-</button>
                <button
                    style={{
                        position: 'absolute',
                        width: '32px',
                        height: '32px',
                        zIndex: 10,
                        top: '0px',
                        left: '84px',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        cursor: 'pointer'
                    }}
                    title={localizationService.toLanguageString('map.reset_zoom', 'NOT_FOUND')}
                    onClick={() => resetTransform()}>
                    <SvgIcon icon={currentLocation} />
                </button>

            </div>
        );
    };

    const primaryDeviceLabel = selectedDevices?.[0] ?? '';

    const getMapList = async () => {

        const commonOperations = async (accessToken: any) => {
            try {
                const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
                const res = await axios.get(`${SERVER_URL}/map/full/list`, { headers });
                const data = res?.data.data;

                if (!data?.length) {
                    setLoading(false);
                    return;
                }

                const mapsList = data;
                const mapNames = data.map((map: any) => map.name);

                setListMaps(mapsList);
                setListMapNames(mapNames);

            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }
            }
        };


        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    //setLoading(false);
                });
        }
    }

    const alignedData = useMemo(() => {
        const primaryDeviceMac = visibleSelectedDevices[0] || selectedDevices[0];
        const primarySeries = visibleStaticPathSeries.find((series: any) => series.deviceMac === primaryDeviceMac) || visibleStaticPathSeries[0];
        const primaryPoints = primarySeries?.points || [];

        if (!primaryPoints.length) return [];

        if (toggleGeoStatic || !mapImageWidth || !mapImageHeight) {
            return primaryPoints.map((point: any) => ({
                coord: [point[1], point[0]],
                date: point[3],
                likelihood: point[5],
            }));
        }

        return primaryPoints
            .map((point: any) => {
                const projected = projectCoordinateToCanvas(point, mapImageWidth, mapImageHeight);
                if (!projected) {
                    return null;
                }

                return {
                    coord: [projected.x / mapImageWidth, 1 - (projected.y / mapImageHeight)],
                    date: point[3],
                    likelihood: point[5],
                };
            })
            .filter((point: any) => Boolean(point));
    }, [selectedDevices, visibleSelectedDevices, visibleStaticPathSeries, toggleGeoStatic, mapImageWidth, mapImageHeight, projectCoordinateToCanvas]);

    useEffect(() => {
        lastSliderIndexRef.current = '';
    }, [alignedData]);

    const updateLines = (startIndex: number, endIndex: number) => {

        const canvas = lineCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        sliderMarkerHoverDataRef.current = [];

        if (!visibleStaticPathSeries.length) {
            return;
        }

        const clampedStartIndex = Math.max(0, Math.min(alignedData.length - 1, startIndex));
        const clampedEndIndex = Math.max(clampedStartIndex, Math.min(alignedData.length - 1, endIndex));
        const startRatio = alignedData.length > 1
            ? Math.max(0, Math.min(1, clampedStartIndex / (alignedData.length - 1)))
            : 0;
        const endRatio = alignedData.length > 1
            ? Math.max(0, Math.min(1, clampedEndIndex / (alignedData.length - 1)))
            : 1;

        drawStaticPathSeries(ctx, canvas.width, canvas.height, startRatio, endRatio);

        const markerCanvas = markerCanvasRef.current;
        if (!markerCanvas) return;

        const ctxMarker = markerCanvas.getContext("2d");
        if (!ctxMarker) return;

        ctxMarker.clearRect(0, 0, markerCanvas.width, markerCanvas.height);

        const ratio = window.devicePixelRatio || 1;
        const targetWidth = mapImageWidth * ratio;
        const targetHeight = mapImageHeight * ratio;
        if (
            markerCanvasSizeRef.current.width !== targetWidth ||
            markerCanvasSizeRef.current.height !== targetHeight
        ) {
            markerCanvas.width = targetWidth;
            markerCanvas.height = targetHeight;
            markerCanvas.style.width = `${mapImageWidth}px`;
            markerCanvas.style.height = `${mapImageHeight}px`;
            markerCanvasSizeRef.current = { width: targetWidth, height: targetHeight, ratio };
        }

        ctxMarker.setTransform(1, 0, 0, 1, 0, 0);
        ctxMarker.scale(ratio, ratio);

        visibleStaticPathSeries.forEach((series: any) => {
            const points = series?.points || [];

            if (!points.length) {
                return;
            }

            const markerIndex = points.length > 1
                ? Math.max(0, Math.min(points.length - 1, Math.round((points.length - 1) * endRatio)))
                : 0;

            const projected = projectCoordinateToCanvas(points[markerIndex], mapImageWidth, mapImageHeight);

            if (!projected) {
                return;
            }

            const baseX = Math.round(projected.x) - 5;
            const baseY = Math.round(projected.y) - 15;

            sliderMarkerHoverDataRef.current.push({
                x: projected.x,
                y: projected.y,
                deviceMac: series.deviceMac,
                color: series.color,
                point: points[markerIndex],
                signalsCount: signalsByDevice[series.deviceMac] ?? points.length,
                signalIndex: markerIndex + 1,
            });

            ctxMarker.fillStyle = series.color;
            ctxMarker.strokeStyle = "white";
            ctxMarker.lineWidth = 1;

            ctxMarker.beginPath();
            ctxMarker.arc(baseX, baseY, 8, Math.PI, 0, false);
            ctxMarker.lineTo(baseX, baseY + 7 * 2.2);
            ctxMarker.closePath();
            ctxMarker.fill();
            ctxMarker.stroke();

            ctxMarker.save();
            ctxMarker.globalCompositeOperation = "destination-out";
            ctxMarker.beginPath();
            ctxMarker.arc(baseX, baseY, 3, 0, 2 * Math.PI);
            ctxMarker.fill();
            ctxMarker.restore();
        });
    };

    const toWakeupTimestamp = (value?: string | number | Date | null): number | null => {
        if (value === null || value === undefined) {
            return null;
        }

        if (value instanceof Date) {
            const ts = value.getTime();
            return Number.isFinite(ts) ? ts : null;
        }

        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        if (typeof value !== 'string') {
            return null;
        }

        const raw = value.trim();
        if (!raw) {
            return null;
        }

        const hasExplicitTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);

        // Native parse only when timezone is explicit.
        if (hasExplicitTimezone) {
            const direct = new Date(raw).getTime();
            if (Number.isFinite(direct)) {
                return direct;
            }
        }

        // "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss" without timezone: treat as UTC.
        const ymdMatch = raw.replace(',', '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
        if (ymdMatch) {
            const [, yyyy, mm, dd, hh = '0', min = '0', ss = '0'] = ymdMatch;
            const parsedUtc = Date.UTC(
                Number(yyyy),
                Number(mm) - 1,
                Number(dd),
                Number(hh),
                Number(min),
                Number(ss),
                0
            );

            return Number.isFinite(parsedUtc) ? parsedUtc : null;
        }

        // "DD/MM/YYYY HH:mm:ss" or "DD/MM/YYYY, HH:mm:ss" without timezone: treat as UTC.
        const dmyMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
        if (dmyMatch) {
            const [, dd, mm, yyyy, hh = '0', min = '0', ss = '0'] = dmyMatch;
            const parsedUtc = Date.UTC(
                Number(yyyy),
                Number(mm) - 1,
                Number(dd),
                Number(hh),
                Number(min),
                Number(ss),
                0
            );

            return Number.isFinite(parsedUtc) ? parsedUtc : null;
        }

        const fallback = new Date(raw).getTime();
        if (Number.isFinite(fallback)) {
            return fallback;
        }

        return null;
    };

    const getPointLocalDateLabel = (pointDate?: any) => {
        const parsedPointTimestamp = toWakeupTimestamp(pointDate ?? null);
        return parsedPointTimestamp === null
            ? (pointDate || '')
            : convertToSystemLocal(new Date(parsedPointTimestamp));
    };

    const applySliderValue = (range: { start: number; end: number }) => {
        if (!alignedData.length) return;

        const clampedStart = Math.max(timeSliderMin, Math.min(timeSliderMax, Number(range?.start ?? timeSliderMin)));
        const clampedEnd = Math.max(timeSliderMin, Math.min(timeSliderMax, Number(range?.end ?? timeSliderMax)));
        const normalizedStart = Math.min(clampedStart, clampedEnd);
        const normalizedEnd = Math.max(clampedStart, clampedEnd);
        const startRatio = (normalizedStart - timeSliderMin) / (timeSliderMax - timeSliderMin);
        const endRatio = (normalizedEnd - timeSliderMin) / (timeSliderMax - timeSliderMin);
        const startIndex = Math.max(0, Math.min(alignedData.length - 1, Math.round(startRatio * (alignedData.length - 1))));
        const endIndex = Math.max(0, Math.min(alignedData.length - 1, Math.round(endRatio * (alignedData.length - 1))));
        const startPoint = alignedData[startIndex];
        const endPoint = alignedData[endIndex];
        if (!startPoint || !endPoint) return;

        const parsedStartTimestamp = toWakeupTimestamp(startPoint?.date ?? null);
        const parsedEndTimestamp = toWakeupTimestamp(endPoint?.date ?? null);
        const readableStartDate = parsedStartTimestamp === null
            ? startPoint.date
            : convertToSystemLocal(new Date(parsedStartTimestamp));
        const readableEndDate = parsedEndTimestamp === null
            ? endPoint.date
            : convertToSystemLocal(new Date(parsedEndTimestamp));

        setProgressiveFromDateLabel(readableStartDate);
        setProgressiveDateLabel(readableEndDate);
        const rangeKey = `${startIndex}-${endIndex}`;
        if (lastSliderIndexRef.current !== rangeKey) {
            lastSliderIndexRef.current = rangeKey;
            updateLines(startIndex, endIndex);
        }
    };

    const handleSliderChange = (event: any) => {
        if (!alignedData.length) return;

        const rawRange = event?.value ?? event?.target?.value ?? event;
        const startValue = Number(rawRange?.start ?? timeSliderMin);
        const endValue = Number(rawRange?.end ?? timeSliderMax);
        if (Number.isNaN(startValue) || Number.isNaN(endValue)) return;

        const nextRange = {
            start: Math.max(timeSliderMin, Math.min(timeSliderMax, startValue)),
            end: Math.max(timeSliderMin, Math.min(timeSliderMax, endValue))
        };
        setSliderRangePercent(nextRange);

        if (sliderFrameRef.current) {
            cancelAnimationFrame(sliderFrameRef.current);
        }

        sliderFrameRef.current = requestAnimationFrame(() => {
            sliderFrameRef.current = null;
            applySliderValue(nextRange);
        });
    };

    const handleGeoSliderDateChange = useCallback((dateLabel?: string) => {
        if (!dateLabel || typeof dateLabel !== 'string') {
            return;
        }

        const parsedPointTimestamp = toWakeupTimestamp(dateLabel);
        const readableDate = parsedPointTimestamp === null
            ? dateLabel
            : convertToSystemLocal(new Date(parsedPointTimestamp));

        setProgressiveDateLabel(readableDate || sliderRangeLabels.to || '');
    }, [sliderRangeLabels.to]);

    useEffect(() => {
        if (!alignedData.length) {
            sliderMarkerHoverDataRef.current = [];

            // All devices hidden: erase the last drawn traces and markers.
            [lineCanvasRef.current, markerCanvasRef.current].forEach((canvas: HTMLCanvasElement | null) => {
                const ctx = canvas?.getContext('2d');
                if (canvas && ctx) {
                    ctx.setTransform(1, 0, 0, 1, 0, 0);
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                }
            });
            return;
        }

        applySliderValue(sliderRangePercent);
    }, [alignedData, sliderRangePercent]);


    useEffect(() => {
        drawAllMarkers();
    }, [drawAllMarkers]);

    const [transformState, setTransformState] = useState<any>([])
    const [tooltipContent, setTooltipContent] = useState("");
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const [tooltipVariant, setTooltipVariant] = useState<'device' | 'device-flipped'>('device');
    const [showTooltip, setShowTooltip] = useState(false);
    const [lastMouseEvent, setLastMouseEvent] = useState(null);
    const transformContentRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setShowTooltip(false);
    }, [showAllPoints]);

    useEffect(() => {
        if (lastMouseEvent) {
            handleDeviceTooltipStaticMap(lastMouseEvent);
        }
    }, [transformState]);

    const resolveTooltipPlacement = (clientX: number, clientY: number) => {
        const estimatedWidth = 340;
        const estimatedHalfHeight = 110;
        const flip = clientX + 24 + estimatedWidth > window.innerWidth;
        const clampedY = Math.min(
            Math.max(clientY, estimatedHalfHeight + 8),
            window.innerHeight - estimatedHalfHeight - 8
        );

        return {
            variant: flip ? 'device-flipped' as const : 'device' as const,
            x: flip ? clientX - 20 : clientX + 20,
            y: clampedY
        };
    };

    //single tooltip 
    useEffect(() => {
        const canvas: HTMLCanvasElement | null = markerCanvasRef.current;
        if (!canvas) return;

        const handleMove = (evt: MouseEvent) => {
            if (!transformContentRef.current) return;
            if (!mapImageWidth || !mapImageHeight) {
                setShowTooltip(false);
                return;
            }

            const hoverItems = sliderMarkerHoverDataRef.current;
            if (!hoverItems?.length) {
                setShowTooltip(false);
                return;
            }

            const rect = transformContentRef.current.getBoundingClientRect();
            const { scale } = transformState;
            const mouseX = (evt.clientX - rect.left) / scale;
            const mouseY = (evt.clientY - rect.top) / scale;

            let nearest: any = null;

            hoverItems.forEach((item: any) => {
                const distance = Math.hypot(mouseX - item.x, mouseY - item.y);
                if (!nearest || distance < nearest.distance) {
                    nearest = { ...item, distance };
                }
            });


            if (nearest && nearest.distance <= 12) {
                const accuracyValue = Number(nearest?.point?.[5]);
                const accuracy = Number.isFinite(accuracyValue) ? accuracyValue.toFixed(2) : '';
                const summary = deviceTooltipSummaryByMac[nearest.deviceMac];
                const currentDateLabel = getPointLocalDateLabel(nearest?.point?.[6]) || '-';
                const nameLabel = languageId === 'it-IT' ? 'Nome' : 'Name';
                const currentLabel = languageId === 'it-IT' ? 'Attuale' : 'Current';
                const signalLabel = languageId === 'it-IT' ? 'Segnale' : 'Signal';
                const tooltipHtml = buildPathHistoryTooltipHtml({
                    title: summary?.name && summary.name !== '-' ? summary.name : nearest.deviceMac,
                    accentColor: summary?.color,
                    rows: [
                        { label: nameLabel, value: summary?.name || '-' },
                        { label: localizationService.toLanguageString('configuration.analysis.path_history.mac', 'Mac'), value: nearest.deviceMac, mono: true },
                        { label: signalLabel, value: `${nearest.signalIndex ?? '-'}/${nearest.signalsCount ?? summary?.signalsCount ?? countWakeups}` },
                        { label: localizationService.toLanguageString('configuration.analysis.path_history.distance', 'Distance'), value: summary?.distance || distance || '-' },
                        { label: localizationService.toLanguageString('configuration.analysis.path_history.first', 'First'), value: summary?.firstDate || '-' },
                        { label: currentLabel, value: currentDateLabel },
                        { label: localizationService.toLanguageString('configuration.analysis.path_history.last', 'Last'), value: summary?.lastDate || '-' },
                        { label: localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'Accuracy'), value: accuracy }
                    ]
                });
                const placement = resolveTooltipPlacement(evt.clientX, evt.clientY);
                setTooltipContent(tooltipHtml);
                setTooltipVariant(placement.variant);
                setTooltipPos({ x: placement.x, y: placement.y });
                setShowTooltip(true);
            } else {
                setShowTooltip(false);
            }
        };

        const handleLeave = () => setShowTooltip(false);

        canvas.addEventListener('mousemove', handleMove);
        canvas.addEventListener('mouseleave', handleLeave);

        return () => {
            canvas.removeEventListener('mousemove', handleMove);
            canvas.removeEventListener('mouseleave', handleLeave);
            if (sliderFrameRef.current) {
                cancelAnimationFrame(sliderFrameRef.current);
            }
        };
    }, [mapImageWidth, mapImageHeight, transformState, localizationService, countWakeups]);

    //tooltips
    const handleDeviceTooltipStaticMap = (e: any) => {

        if (!showAllPoints) return;

        setLastMouseEvent(e);
        if (!transformContentRef.current) return;
        if (!mapImageWidth || !mapImageHeight) return;
        if (!visibleStaticPathSeries.length) return;

        const { scale } = transformState;
        const rect = transformContentRef.current.getBoundingClientRect();

        // Coordinate del mouse relative all'immagine, corrette per lo zoom
        const mouseX = (e.clientX - rect.left) / scale;
        const mouseY = (e.clientY - rect.top) / scale;

        const radius = 10;
        let nearest: any = null;

        visibleStaticPathSeries.forEach((series: any) => {
            const points = Array.isArray(series?.points) ? series.points : [];

            points.forEach((point: any, pointIndex: number) => {
                const projected = projectCoordinateToCanvas(point, mapImageWidth, mapImageHeight);

                if (!projected) {
                    return;
                }

                const distance = Math.hypot(mouseX - projected.x, mouseY - projected.y);

                if (distance <= radius && (!nearest || distance < nearest.distance)) {
                    nearest = {
                        distance,
                        deviceMac: series.deviceMac,
                        signalsCount: signalsByDevice[series.deviceMac] ?? points.length,
                        signalIndex: pointIndex + 1,
                        point,
                    };
                }
            });
        });

        if (nearest) {
            const accuracyValue = Number(nearest?.point?.[5]);
            const accuracy = Number.isFinite(accuracyValue) ? accuracyValue.toFixed(2) : '';
            const summary = deviceTooltipSummaryByMac[nearest.deviceMac];
            const currentDateLabel = getPointLocalDateLabel(nearest?.point?.[6]) || '-';
            const nameLabel = languageId === 'it-IT' ? 'Nome' : 'Name';
            const currentLabel = languageId === 'it-IT' ? 'Attuale' : 'Current';
            const signalLabel = languageId === 'it-IT' ? 'Segnale' : 'Signal';
            const tooltipStr = buildPathHistoryTooltipHtml({
                title: summary?.name && summary.name !== '-' ? summary.name : nearest.deviceMac,
                accentColor: summary?.color,
                rows: [
                    { label: nameLabel, value: summary?.name || '-' },
                    { label: localizationService.toLanguageString('configuration.analysis.path_history.mac', 'Mac'), value: nearest.deviceMac, mono: true },
                    { label: signalLabel, value: `${nearest.signalIndex ?? '-'}/${nearest.signalsCount ?? summary?.signalsCount ?? 0}` },
                    { label: localizationService.toLanguageString('configuration.analysis.path_history.distance', 'Distance'), value: summary?.distance || distance || '-' },
                    { label: localizationService.toLanguageString('configuration.analysis.path_history.first', 'First'), value: summary?.firstDate || '-' },
                    { label: currentLabel, value: currentDateLabel },
                    { label: localizationService.toLanguageString('configuration.analysis.path_history.last', 'Last'), value: summary?.lastDate || '-' },
                    { label: localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'Accuracy'), value: accuracy }
                ]
            });
            const placement = resolveTooltipPlacement(e.clientX, e.clientY);
            setTooltipContent(tooltipStr);
            setTooltipVariant(placement.variant);
            setTooltipPos({ x: placement.x, y: placement.y });
            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    const toDateSafe = (value: any) => {
        if (!value) return null;

        if (value instanceof Date) return value; // giÃ  una Date

        if (typeof value === 'string') {
            // tenta di interpretare la stringa DD/MM/YYYY HH:mm:ss
            const [datePart, timePart] = value.split(' ');
            if (!datePart) return null;
            const [day, month, year]: any = datePart.replace(',', '').split('/');
            const [hour = 0, minute = 0, second = 0]: any = (timePart || '').split(':');

            const d: any = new Date(year, month - 1, day, hour, minute, second);
            return isNaN(d.getTime()) ? null : d;
        }

        return null;
    };

    type DeviceOption = { mac: string; name: string; displayName: string; searchKey: string };
    const DEVICE_PAGE_SIZE = 30;
    const MAX_VISIBLE_DEVICE_TAGS = 3;

    const [filter, setFilter] = useState("");
    const [deviceListSkip, setDeviceListSkip] = useState(0);
    const deviceSelectRef = useRef<any>(null);
    const lastDeviceHeaderInteractionRef = useRef(0);

    const deviceOptions = useMemo<DeviceOption[]>(() => {
        return (devicesList || [])
            .filter((d: any) => d && typeof d.mac === 'string' && (!checkedOnlyBound || d.name != null))
            .map((d: any) => {
                const name = typeof d.name === 'string' ? d.name.trim() : '';
                return {
                    mac: d.mac,
                    name,
                    displayName: name ? `${name} (${d.mac})` : d.mac,
                    searchKey: `${name} ${d.mac}`.toLowerCase()
                };
            });
    }, [devicesList, checkedOnlyBound]);

    const filteredDevices = useMemo(() => {
        const lower = filter.trim().toLowerCase();
        return lower ? deviceOptions.filter((d) => d.searchKey.includes(lower)) : deviceOptions;
    }, [deviceOptions, filter]);

    const visibleDevicePage = useMemo(
        () => filteredDevices.slice(deviceListSkip, deviceListSkip + DEVICE_PAGE_SIZE),
        [filteredDevices, deviceListSkip]
    );

    const selectedDeviceSet = useMemo(() => new Set(selectedDevices), [selectedDevices]);

    const selectedDeviceOptions = useMemo(
        () => deviceOptions.filter((d) => selectedDeviceSet.has(d.mac)),
        [deviceOptions, selectedDeviceSet]
    );

    const deviceNameByMac = useMemo(() => {
        return deviceOptions.reduce((acc: Record<string, string>, option: DeviceOption) => {
            acc[option.mac] = option.name;
            return acc;
        }, {});
    }, [deviceOptions]);

    const deviceTooltipSummaryByMac = useMemo(() => {
        const summaries: Record<string, {
            name: string;
            mac: string;
            signalsCount: number;
            distance: string;
            firstDate: string;
            lastDate: string;
            color: string;
        }> = {};

        staticPathSeries.forEach((series: any) => {
            const points = Array.isArray(series?.points) ? series.points : [];
            const firstPoint = points[0];
            const lastPoint = points[points.length - 1];
            const mac = series.deviceMac;

            summaries[mac] = {
                name: deviceNameByMac[mac] || '-',
                mac,
                signalsCount: signalsByDevice[mac] ?? points.length,
                distance: distanceByDevice[mac] ?? distance,
                firstDate: getPointLocalDateLabel(firstPoint?.[3]) || '-',
                lastDate: getPointLocalDateLabel(lastPoint?.[4] ?? lastPoint?.[3]) || '-',
                color: series.color || '#198692'
            };
        });

        return summaries;
    }, [staticPathSeries, deviceNameByMac, signalsByDevice, distanceByDevice, distance]);

    const filteredSelectedCount = useMemo(
        () => filteredDevices.reduce((count, d) => count + (selectedDeviceSet.has(d.mac) ? 1 : 0), 0),
        [filteredDevices, selectedDeviceSet]
    );

    const allFilteredSelected = filteredDevices.length > 0 && filteredSelectedCount === filteredDevices.length;
    const someFilteredSelected = filteredSelectedCount > 0 && !allFilteredSelected;
    const selectAllExceedsLimit = !allFilteredSelected &&
        selectedDevices.length - filteredSelectedCount + filteredDevices.length > COUNTER_MAX_DEVICES;

    const toggleSelectAllFiltered = () => {
        if (allFilteredSelected) {
            const filteredMacs = new Set(filteredDevices.map((d) => d.mac));
            applyDeviceSelection(selectedDevices.filter((mac) => !filteredMacs.has(mac)));
            return;
        }

        const next = new Set(selectedDevices);
        filteredDevices.forEach((d) => next.add(d.mac));
        applyDeviceSelection(Array.from(next));
    };

    const deviceTags = selectedDeviceOptions.length > MAX_VISIBLE_DEVICE_TAGS
        ? [{
            text: `${selectedDeviceOptions.length} ${localizationService.toLanguageString('configuration.analysis.heatmap.devices_selected_count', 'devices selected')}`,
            data: selectedDeviceOptions
        }]
        : undefined;

    const selectAllLimitMessage = localizationService
        .toLanguageString('configuration.analysis.heatmap.select_all_limit', 'Max #sym:maxDevices devices: narrow the search to select all.')
        .replace(/#sym:maxDevices/g, String(COUNTER_MAX_DEVICES));

    const analyzeBlockReason = selectedDevices.length === 0
        ? localizationService.toLanguageString('configuration.analysis.heatmap.hint_devices', 'Select at least one device.')
        : !checked
            ? localizationService.toLanguageString('configuration.analysis.heatmap.hint_interval', 'Pick a time interval.')
            : '';

    const handleChange = useCallback(
        (e: RadioButtonChangeEvent) => {
            setGroupingType(e.value);
        },
        [setGroupingType]
    );

    const handleValueChange = (type: 'near' | 'each', value: number) => {
        setGroupingValues((prev) => ({ ...prev, [type]: value }));
    };

    const handleAnalyzeClick = () => {
        setSliderRangePercent({ start: timeSliderMin, end: timeSliderMax });
        checkResults();
    };

    const toggleDeviceVisibility = (deviceMac: string) => {
        setHiddenDevices((prev) => ({
            ...prev,
            [deviceMac]: !prev[deviceMac]
        }));
    };

    const checkResults = async (mapId?: any, isGrouped?: boolean) => {

        setLoading(true);
        setCoordinates([]);
        setShowColor(false);

        const { startDate, endDate } = buildUtcRangeForRequests();
        setToDateLabel(sliderRangeLabels.to)
        setProgressiveDateLabel('')
        setProgressiveFromDateLabel('')

        const commonOperations = async (accessToken: any) => {
            try {
                const params = {
                    startDate,
                    endDate,
                    macs: selectedDevices
                };
                const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
                const res = await axios.post(`${SERVER_URL}/wakeup/logs/count`, params, { headers });
                const data = Number(res?.data?.data ?? 0);

                setCountWakeups(data);
                if (data <= COUNTER_MAX_POINTS) {
                    if (data == 0) {
                        setIsValid(false)
                        setLoading(false);
                        setMessageNoData(true);

                        setTimeout(() => {
                            setMessageNoData(false);
                        }, 3000);
                    } else {
                        setIsValid(true)
                        await retrieveWakeups(mapId, isGrouped);
                    }
                } else {
                    setIsValid(false)
                    setChecked(false)
                    setMessageTooData(true);
                    setAnalyzeEnabled(false);
                    setTimeout(() => {
                        setMessageTooData(false);
                    }, 3000);
                    setLoading(false);
                    return;
                }

            } catch (error: any) {
                const errorMessage = error?.response?.data?.errorMessage || '';
                if (errorMessage.includes('Start date must be before end date')) {
                    setError(true);
                    if (languageId == 'it-IT') {
                        setErrorMessage("Errore: La data di inizio deve essere prima della data di fine");
                    } else {
                        setErrorMessage("Error: Start date must be before end date");
                    }
                } else if (errorMessage.includes('There are no wakeups available for the selected map')) {
                    setWarningMessage(true);
                    if (languageId == 'it-IT') {
                        setErrorMessage("Non ci sono wakeup disponibili per la mappa selezionata");
                    } else {
                        setErrorMessage("There are no wakeups available for the selected map");
                    }
                } else {
                    setError(true);
                    if (languageId == 'it-IT') {
                        setErrorMessage("Errore sconosciuto durante il controllo dei risultati");
                    } else {
                        setErrorMessage("Unknown error during results check");
                    }
                }
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }
                setTimeout(() => {
                    setError(false);
                }, 3000);
                if (!warningMessage) {
                    setTimeout(() => {
                        setWarningMessage(false);
                    }, 3000);
                }
                setLoading(false);
                return;
            }
        };


        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                });
        }

    }

    const getEmptyAccuracyChartData = useCallback(() => ([
        { category: localizationService.toLanguageString('configuration.analysis.path_history.very_low', 'Very low'), value: 0, color: "#ff0000" },
        { category: localizationService.toLanguageString('configuration.analysis.path_history.low', 'Low'), value: 0, color: "orange" },
        { category: localizationService.toLanguageString('configuration.analysis.path_history.medium', 'Medium'), value: 0, color: "yellow" },
        { category: localizationService.toLanguageString('configuration.analysis.path_history.good', 'Good'), value: 0, color: "green" },
        { category: localizationService.toLanguageString('configuration.analysis.path_history.optimal', 'Optimal'), value: 0, color: "lightgreen" },
    ]), [localizationService]);

    const accuracyChartDataByDevice = useMemo(() => {
        const chartDataByDevice: Record<string, any[]> = {};

        staticPathSeries.forEach((series: any) => {
            const points = Array.isArray(series?.points) ? series.points : [];
            const colorCount = { red: 0, orange: 0, yellow: 0, green: 0, lightgreen: 0 };

            points.forEach(([, , , , , likelihood]: any) => {
                const color = getLikelihoodColor(likelihood);
                if (colorCount[color as keyof typeof colorCount] !== undefined) {
                    colorCount[color as keyof typeof colorCount]++;
                }
            });

            chartDataByDevice[series.deviceMac] = [
                { category: localizationService.toLanguageString('configuration.analysis.path_history.very_low', 'Very low'), value: colorCount.red, color: "#ff0000" },
                { category: localizationService.toLanguageString('configuration.analysis.path_history.low', 'Low'), value: colorCount.orange, color: "orange" },
                { category: localizationService.toLanguageString('configuration.analysis.path_history.medium', 'Medium'), value: colorCount.yellow, color: "yellow" },
                { category: localizationService.toLanguageString('configuration.analysis.path_history.good', 'Good'), value: colorCount.green, color: "green" },
                { category: localizationService.toLanguageString('configuration.analysis.path_history.optimal', 'Optimal'), value: colorCount.lightgreen, color: "lightgreen" },
            ];
        });

        return chartDataByDevice;
    }, [staticPathSeries, localizationService]);

    useEffect(() => {
        const hasCoordinates = visibleStaticPathSeries.length > 0;

        if (isValid && selectedMap && hasCoordinates) {
            renderSelectedMap(selectedMap);
        }
    }, [showColor, isValid, selectedMap, visibleStaticPathSeries]);

    useEffect(() => {
        if (showZone && selectedMap) {
            getZoneList(selectedMap.id, mapGeoreferenced);
        }
    }, [showZone, selectedMap, mapGeoreferenced, showColor]);

    useEffect(() => {
        return () => {
            if (maxDevicesMessageTimerRef.current !== null) {
                window.clearTimeout(maxDevicesMessageTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        setToDateLabel(sliderRangeLabels.to || '');
        setProgressiveDateLabel('');
        setProgressiveFromDateLabel('');
    }, [sliderRangeLabels.to]);

    const refreshDevices = () => {
        setIsDeviceMultiSelectOpen(false)
        setMapName([])
        setFilter('')
        setAnalyzeEnabled(false)
        setIntervalTimeValue(null)
        setIntervalTimeName(null)
        setChecked(false)
        setIsValid(false)
        setMessageMaxDevices(false)
        setSelectedDevices([])
        getDeviceList(true)
        setIsAdvanced(false)
    }

    const spaghettiExtraCss = `
        .spaghetti-static-map {
            border: 1px solid #bfd4e8;
            border-radius: 12px;
            overflow: hidden;
            background: #ffffff;
        }
    `;
    const isMapGeoreferencedFlag = (value: any) => {
        return value === true || value === 1 || value === '1';
    };

    const convertGeoPointsToCanvasPoints = useCallback((geoPoints: any[] = [], mapInfo?: any) => {

        const canvasWidth = lineCanvasRef.current?.width || mapImageWidth || 0;

        const canvasHeight = lineCanvasRef.current?.height || mapImageHeight || 0;

        if (!canvasWidth || !canvasHeight || !geoPoints.length || !mapInfo) {
            return [];
        }

        const { botLeft, botRight, topLeft } = mapInfo;

        if (!botLeft || !botRight || !topLeft) {
            return [];
        }

        const lonSpan = Number(botRight.longitude) - Number(botLeft.longitude);

        const latSpan = Number(topLeft.latitude) - Number(botLeft.latitude);

        if (!lonSpan || !latSpan) {
            return [];
        }

        return geoPoints.map((point: any) => {

            const longitude = Number(point[1]);
            const latitude = Number(point[0]);

            const x = canvasWidth * (longitude - Number(botLeft.longitude)) / lonSpan;

            const y = canvasHeight * (Number(topLeft.latitude) - latitude) / latSpan;

            return { x, y, raw: point };
        });
    }, [mapImageWidth, mapImageHeight]);

    const onToggleGeoStatic = async (e: any) => {

        const toggleValue: any = e.value;

        localStorage.setItem('onToggleGeoStatic', JSON.stringify(toggleValue));

        const mapSupportsGeo = isMapGeoreferencedFlag(selectedMap?.georeferenced);

        const shouldShowGeo = Boolean(mapSupportsGeo && toggleValue);

        localStorage.setItem('mapGeoreferenced', JSON.stringify(shouldShowGeo));

        setMapGeoreferenced(shouldShowGeo);
        setToggleGeoStatic(toggleValue);

        /**
         * GEO -> STATIC
         */
        if (!shouldShowGeo) {
            if (selectedMap) {
                renderSelectedMap(selectedMap);
            }
            return;
        }

        /**
         * STATIC -> GEO
         */
        if (toggleValue) {
            drawAllMarkers();
            return;
        }
    };

    useEffect(() => {

        const primaryDevice = visibleSelectedDevices[0] || selectedDevices[0];
        const primarySeries = visibleStaticPathSeries.find((series: any) => series.deviceMac === primaryDevice) || visibleStaticPathSeries[0];

        const coords = primarySeries?.points;

        if (!coords?.length) return;

        if (toggleGeoStatic) {

            setRenderCoordinates(coords.map((point: any) => ({
                x: point[1],
                y: point[0],
                raw: point
            })));

            return;
        }

        const converted = convertGeoPointsToCanvasPoints(coords, selectedMap);

        setRenderCoordinates(converted);

    }, [mapGeoreferenced, visibleStaticPathSeries, visibleSelectedDevices, selectedDevices, selectedMap, showColor, toggleGeoStatic, convertGeoPointsToCanvasPoints]);

    return (
        <>
            <style>{analysisPanelCss + spaghettiExtraCss}</style>

            {loading && loadingPanel}
            {devicesList?.length == 0 || devicesList == undefined &&
                <div style={{ textAlign: 'center', padding: '20px', border: '1px dashed #ccc' }}>
                    <p>{localizationService.toLanguageString('configuration.devices.no_data', 'NOT_FOUND')}</p>
                </div>
            }
            {devicesList?.length > 0 &&
                <div
                    className="heatmap-layout"
                    style={{ ...analysisLayoutStyle }}
                >
                    <NotificationGroup
                        style={{
                            right: 0,
                            bottom: 50,
                            zIndex: 1200,
                            alignItems: "flex-start",
                            flexWrap: "wrap-reverse",
                        }}
                    >
                        <Fade enter={true} exit={true}>
                            {spaghettiChartMessage && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setSpaghettiChartMessage(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.analysis.path_history.map_loaded', 'NOT_FOUND')}</span>
                                </Notification>
                            )}
                            {messageNoData &&
                                <Notification
                                    type={{ style: "warning", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageNoData(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.analysis.path_history.no_data', 'NOT_FOUND')}</span>
                                </Notification>
                            }
                            {messageTooData &&
                                <Notification
                                    type={{ style: "warning", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageTooData(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.analysis.path_history.too_data', 'NOT_FOUND')}</span>
                                </Notification>
                            }
                            {messageMaxDevices &&
                                <Notification
                                    type={{ style: "warning", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageMaxDevices(false)}
                                >
                                    <span>{maxDevicesMessage}</span>
                                </Notification>
                            }
                            {error &&
                                <Notification
                                    type={{ style: "error", icon: true }}
                                    closable={true}
                                    onClose={() => setError(false)}
                                >
                                    <span>{errorMessage}</span>
                                    <span>{localizationService.toLanguageString('configuration.analysis.path_history.no_data_map', 'NOT_FOUND')}</span>
                                </Notification>
                            }
                            {warningMessage &&
                                <Notification
                                    type={{ style: "warning", icon: true }}
                                    closable={true}
                                    onClose={() => setWarningMessage(false)}
                                >
                                    <span>{errorMessage}</span>
                                </Notification>
                            }
                        </Fade>
                    </NotificationGroup>

                    {/* left panel */}
                    <div
                        className="heatmap-control-panel"
                        style={{ ...analysisControlPanelStyle }}
                    >
                        {/* 1. devices */}
                        <div className="heatmap-section">
                            <div className="heatmap-section-title">
                                {localizationService.toLanguageString('configuration.analysis.heatmap.section_devices', 'Devices')}
                                {selectedDevices.length > 0 && ` (${selectedDevices.length}/${COUNTER_MAX_DEVICES})`}
                            </div>

                            <div className="heatmap-device-picker">
                            <MultiSelect
                                ref={deviceSelectRef}
                                className="heatmap-device-select"
                                data={visibleDevicePage}
                                dataItemKey="mac"
                                textField="displayName"
                                autoClose={false}
                                opened={isDeviceMultiSelectOpen}
                                onOpen={() => setIsDeviceMultiSelectOpen(true)}
                                onClose={() => {
                                    // Header controls steal focus and blur the input: that is not a real close.
                                    if (Date.now() - lastDeviceHeaderInteractionRef.current < 400) {
                                        return;
                                    }
                                    setIsDeviceMultiSelectOpen(false);
                                }}
                                filterable={true}
                                filter={filter}
                                onFilterChange={(e) => {
                                    setFilter(e.filter?.value || '');
                                    setDeviceListSkip(0);
                                }}
                                virtual={{
                                    total: filteredDevices.length,
                                    skip: deviceListSkip,
                                    pageSize: DEVICE_PAGE_SIZE
                                }}
                                onPageChange={(e: any) => setDeviceListSkip(e.page.skip)}
                                popupSettings={{ height: '320px' }}
                                value={selectedDeviceOptions}
                                tags={deviceTags}

                                onChange={(e: any) => {
                                    const nextMacs: string[] = (e.value || [])
                                        .map((d: any) => (typeof d === 'string' ? d : d?.mac))
                                        .filter((mac: any): mac is string => typeof mac === 'string' && mac.length > 0);

                                    if (nextMacs.length > COUNTER_MAX_DEVICES) {
                                        showMaxDevicesWarning();
                                        return;
                                    }

                                    applyDeviceSelection(nextMacs);

                                    if (nextMacs.length >= COUNTER_MAX_DEVICES) {
                                        setIsDeviceMultiSelectOpen(false);
                                    }
                                }}

                                header={
                                    // preventDefault keeps focus in the input so the popup stays open.
                                    <div
                                        className="heatmap-select-all"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            lastDeviceHeaderInteractionRef.current = Date.now();
                                        }}
                                    >
                                        <label htmlFor="spaghettiOnlyBound" className="heatmap-inline-field heatmap-popup-toggle">
                                            <span>{localizationService.toLanguageString('configuration.analysis.path_history.only_binded', 'NOT_FOUND')}</span>
                                            <Switch
                                                id="spaghettiOnlyBound"
                                                onChange={(e: any) => {
                                                    lastDeviceHeaderInteractionRef.current = Date.now();
                                                    switchOnlyBound(Boolean(e.value));
                                                    deviceSelectRef.current?.focus?.();
                                                }}
                                                checked={checkedOnlyBound}
                                                size={'small'}
                                            />
                                        </label>
                                        <button
                                            type="button"
                                            className="heatmap-select-all-btn"
                                            disabled={filteredDevices.length === 0 || selectAllExceedsLimit}
                                            onClick={toggleSelectAllFiltered}
                                        >
                                            <input
                                                type="checkbox"
                                                className="k-checkbox k-checkbox-md k-rounded-md"
                                                checked={allFilteredSelected}
                                                ref={(el) => { if (el) el.indeterminate = someFilteredSelected; }}
                                                readOnly
                                                tabIndex={-1}
                                                aria-hidden="true"
                                            />
                                            <span>
                                                {filter.trim()
                                                    ? localizationService.toLanguageString('configuration.analysis.heatmap.select_all_filtered', 'Select all results')
                                                    : localizationService.toLanguageString('configuration.analysis.heatmap.select_all', 'Select all')}
                                                {` (${filteredDevices.length})`}
                                            </span>
                                        </button>
                                        {selectAllExceedsLimit && (
                                            <div className="heatmap-field-hint">{selectAllLimitMessage}</div>
                                        )}
                                    </div>
                                }

                                footer={
                                    <div className="heatmap-device-footer">
                                        {`${filteredDevices.length} / ${deviceOptions.length} ${localizationService.toLanguageString('configuration.analysis.heatmap.devices_shown', 'devices shown')}`}
                                    </div>
                                }

                                itemRender={(li: any, itemProps: any) => {
                                    const device: DeviceOption = itemProps.dataItem;
                                    if (!device?.mac) return li;

                                    return React.cloneElement(
                                        li,
                                        { ...li.props, className: `${li.props.className ?? ''} heatmap-device-item` },
                                        <>
                                            <input
                                                type="checkbox"
                                                className="k-checkbox k-checkbox-md k-rounded-md"
                                                checked={selectedDeviceSet.has(device.mac)}
                                                readOnly
                                                tabIndex={-1}
                                                aria-hidden="true"
                                            />
                                            <span className="heatmap-device-name">{device.name || device.mac}</span>
                                            {device.name && <span className="heatmap-device-mac">{device.mac}</span>}
                                        </>
                                    );
                                }}

                                placeholder={localizationService.toLanguageString('configuration.analysis.path_history.select_device', 'NOT_FOUND')}
                            />

                            <Button
                                className="plusParam heatmap-action-btn"
                                style={{ marginTop: '0px' }}
                                onClick={() => {
                                    refreshDevices();
                                }}
                                title={localizationService.toLanguageString('configuration.analysis.path_history.refresh_list', 'NOT_FOUND')}
                            >
                                <Avatar className="plusParam">
                                    <SvgIcon icon={arrowRotateCwIcon} style={{ margin: '8px' }} />
                                </Avatar>
                            </Button>
                            </div>
                        </div>

                        {/* 2. time interval */}
                        <div className="heatmap-section">
                            <div className="heatmap-section-title">
                                {localizationService.toLanguageString('configuration.analysis.heatmap.section_interval', 'Time interval')}
                            </div>
                            <DropDownList
                                style={{ width: '100%' }}
                                data={intervalTimeList.data}
                                textField="name"
                                value={intervalTimeName}
                                disabled={selectedDevices.length == 0}
                                onChange={onChangeIntervalTime}
                            />

                                {
                                    //advanced timeslot
                                    isAdvanced &&
                                    <div style={{ display: 'grid', width: '350px' }}>
                                        <div>
                                            <DatePicker
                                                name="fromDate"
                                                format="dd/MM/yyyy"
                                                validityStyles={false}
                                                label={localizationService.toLanguageString('configuration.analysis.path_history.from_date', 'NOT_FOUND')}
                                                value={dateTimeState.fromDate}
                                                width={160}
                                                required={true}
                                                onChange={handleChangeDate}
                                                onBlur={handleBlurDate}
                                                validationMessage={
                                                    dateTimeState.fromDate === null
                                                        ? `${localizationService.toLanguageString('configuration.analysis.path_history.from_date', 'NOT_FOUND')}`
                                                        : ""
                                                }
                                            />

                                            <DatePicker
                                                name="toDate"
                                                format="dd/MM/yyyy"
                                                style={{ marginLeft: '20px' }}
                                                validityStyles={false}
                                                label={localizationService.toLanguageString('configuration.analysis.path_history.to_date', 'NOT_FOUND')}
                                                value={dateTimeState.toDate}
                                                width={160}
                                                required={true}
                                                min={dateTimeState.fromDate || undefined}
                                                onChange={handleChangeDate}
                                                onBlur={handleBlurDate}
                                                validationMessage={
                                                    dateTimeState.toDate === null
                                                        ? `${localizationService.toLanguageString('configuration.analysis.path_history.to_date', 'NOT_FOUND')}`
                                                        : ""
                                                }
                                            />
                                        </div>

                                        <div style={{ display: 'flex', margin: '10px 0 10px 0' }}>
                                            <div>
                                                <TimePicker
                                                    format="HH:mm:ss"
                                                    name="fromHour"
                                                    validityStyles={false}
                                                    value={dateTimeState.fromHour}
                                                    width={160}
                                                    required={true}
                                                    onChange={handleChangeTime}
                                                    onBlur={handleBlurTime}
                                                    validationMessage={
                                                        dateTimeState.fromHour === null
                                                            ? `${localizationService.toLanguageString('configuration.analysis.path_history.from_hour', 'NOT_FOUND')}`
                                                            : ""
                                                    }
                                                />
                                            </div>
                                            <div style={{ marginLeft: '20px' }}>
                                                <TimePicker
                                                    format="HH:mm:ss"
                                                    name="toHour"
                                                    validityStyles={false}
                                                    value={dateTimeState.toHour}
                                                    width={160}
                                                    required={true}
                                                    min={dateTimeState.fromHour || undefined}
                                                    valid={(() => {
                                                        const { fromHour, toHour } = dateTimeState || {};

                                                        const from = toDateSafe(fromHour);
                                                        const to = toDateSafe(toHour);

                                                        if (!from || !to) return false;
                                                        return from.getTime() < to.getTime();
                                                    })()}
                                                    onChange={handleChangeTime}
                                                    onBlur={handleBlurTime}
                                                    validationMessage={
                                                        dateTimeState.toHour === null
                                                            ? `${localizationService.toLanguageString('configuration.analysis.path_history.to_hour', 'NOT_FOUND')}`
                                                            : `${localizationService.toLanguageString('configuration.analysis.path_history.to_hour', 'NOT_FOUND')}`
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                }

                        </div>

                        {/* 3. run analysis */}
                        <div className="heatmap-section">
                            <Button
                                className="heatmap-analyze-btn heatmap-action-btn"
                                svgIcon={chartScatterSmoothLinesMarkersIcon}
                                disabled={analyzeBlockReason !== ''}
                                onClick={handleAnalyzeClick}
                            >
                                {localizationService.toLanguageString('configuration.analysis.path_history.analyze', 'NOT_FOUND')}
                            </Button>
                            {analyzeBlockReason !== '' && (
                                <div className="heatmap-hint" role="status">{analyzeBlockReason}</div>
                            )}
                        </div>

                        {/* 4. display options, available once data is loaded */}
                        {analyzeEnabled && !noData && (
                            <div className="heatmap-section">
                                <div className="heatmap-section-title">
                                    {localizationService.toLanguageString('configuration.analysis.heatmap.section_view', 'Display')}
                                </div>

                                <div className="heatmap-field-label">
                                    {localizationService.toLanguageString('configuration.analysis.path_history.maps', 'NOT_FOUND')}
                                </div>
                                <DropDownList
                                    style={{ width: '100%' }}
                                    data={listMapNames}
                                    value={mapName.length > 0 ? mapName : undefined}
                                    onChange={(e: any) => changeMap(e)}
                                />

                                {selectedMap?.georeferenced == 1 && selectedMap?.image != '' && (
                                    <label htmlFor="spaghettiGeoStatic" className="heatmap-inline-field" style={{ marginTop: '8px' }}>
                                        <Switch
                                            id="spaghettiGeoStatic"
                                            onChange={onToggleGeoStatic}
                                            checked={toggleGeoStatic}
                                        />
                                        <span>
                                            {toggleGeoStatic
                                                ? localizationService.toLanguageString('map.geo', 'NOT_FOUND')
                                                : localizationService.toLanguageString('map.static', 'NOT_FOUND')}
                                        </span>
                                    </label>
                                )}

                                <div className="heatmap-switch-row">
                                    <label htmlFor="spaghettiShowZones" className="heatmap-inline-field">
                                        <span>{localizationService.toLanguageString('configuration.analysis.path_history.show_zones', 'NOT_FOUND')}</span>
                                        <Switch
                                            id="spaghettiShowZones"
                                            onChange={() => {
                                                setShowZone(!showZone)
                                                if (mapGeoreferenced) {
                                                    setShowZoneMapGeo([])
                                                } else {
                                                    const zoneCtx = zoneCanvasRef?.current?.getContext("2d");
                                                    const zoneWidth = zoneCanvasRef?.width ?? zoneCanvasRef?.current?.width
                                                    const zoneHeight = zoneCanvasRef?.height ?? zoneCanvasRef?.current?.height
                                                    zoneCtx?.clearRect(0, 0, zoneWidth, zoneHeight)
                                                }
                                            }}
                                            checked={showZone}
                                            size={'large'}
                                        />
                                    </label>

                                    <label htmlFor="spaghettiShowAllMarkers" className="heatmap-inline-field">
                                        <span>{localizationService.toLanguageString('configuration.analysis.path_history.show_all_markers', 'NOT_FOUND')}</span>
                                        <Switch
                                            id="spaghettiShowAllMarkers"
                                            onChange={() => setShowAllPoints(!showAllPoints)}
                                            checked={showAllPoints}
                                            size={'large'}
                                        />
                                    </label>
                                </div>

                                <div
                                    className="heatmap-field-label"
                                    title={localizationService.toLanguageString('configuration.analysis.path_history.colorize_tooltip', 'NOT_FOUND')}
                                >
                                    {localizationService.toLanguageString('configuration.analysis.path_history.colorize', 'NOT_FOUND')}
                                </div>
                                <ButtonGroup className="heatmap-type-group">
                                    <Button togglable={true} selected={!showColor} onClick={() => setShowColor(false)}>
                                        {localizationService.toLanguageString('configuration.analysis.path_history.mac', 'NOT_FOUND')}
                                    </Button>
                                    <Button togglable={true} selected={showColor} onClick={() => setShowColor(true)}>
                                        {localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'NOT_FOUND')}
                                    </Button>
                                </ButtonGroup>

                                {/* slider */}
                                <div style={{ marginTop: '12px', width: '250px' }}>
                                    <div
                                        style={{
                                            marginBottom: '6px',
                                            opacity: `${!analyzeEnabled || noData || selectedDevices.length == 0 || signalsGrouping || showAllPoints ? '0.5' : '1'}`,
                                            fontWeight: 600,
                                            fontSize: '13px'
                                        }}
                                    >
                                        {localizationService.toLanguageString('azure.interval', 'NOT_FOUND')}
                                    </div>

                                    <RangeSlider
                                        className="heatmap-point-size-slider"
                                        style={{ width: '250px' }}
                                        min={timeSliderMin}
                                        max={timeSliderMax}
                                        step={1}
                                        value={sliderRangePercent}
                                        onChange={handleSliderChange}
                                        disabled={!analyzeEnabled || noData || selectedDevices.length == 0 || signalsGrouping || showAllPoints}
                                    />

                                    {isValid && (
                                    <div
                                        style={{
                                            marginTop: '8px',
                                            opacity: `${!analyzeEnabled || noData || selectedDevices.length == 0 || signalsGrouping || showAllPoints ? '0.5' : '1'}`,
                                            fontSize: '12px',
                                            color: '#555',
                                            width: '250px'
                                        }}
                                    >
                                        {`${spaghettiProgressiveFrom || ''}${spaghettiProgressiveFrom && spaghettiProgressiveTo ? ' - ' : ''}${spaghettiProgressiveTo || ''}`}
                                    </div>
                                    )}
                                </div>

                                {/*signals grouping*/}
                                <div style={{ display: 'inline-block', marginTop: '10px' }}>
                                    <Tooltip position="top" anchorElement="target">
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span
                                                style={{ width: '180px', marginRight: '10px', opacity: `${!analyzeEnabled || noData || selectedDevices.length == 0 ? '0.5' : '1'}` }}
                                                title={localizationService.toLanguageString(
                                                    'configuration.analysis.path_history.signals_grouping_tooltip',
                                                    'NOT_FOUND'
                                                )}
                                            >
                                                {localizationService.toLanguageString(
                                                    'configuration.analysis.path_history.signals_grouping',
                                                    'NOT_FOUND'
                                                )}
                                            </span>
                                            <Switch
                                                onChange={() => {
                                                    setSignalsGrouping(!signalsGrouping)
                                                    setGroupingValues({ near: 0, each: 0 })
                                                    if (signalsGrouping) {
                                                        checkResults(undefined, !signalsGrouping)
                                                    }
                                                }}
                                                checked={signalsGrouping}
                                                disabled={noData || selectedDevices.length == 0}
                                                size={'large'}
                                            />
                                        </div>
                                    </Tooltip>
                                </div>

                                {/*average positions */}
                                {signalsGrouping && (
                                    <div style={{ display: 'grid', marginTop: '10px' }}>
                                        <Tooltip position="top" anchorElement="target">
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span
                                                    style={{ width: '180px', marginRight: '10px' }}
                                                    title={localizationService.toLanguageString(
                                                        'configuration.analysis.path_history.average_positions_tooltip',
                                                        'NOT_FOUND'
                                                    )}
                                                >
                                                    {localizationService.toLanguageString(
                                                        'configuration.analysis.path_history.average_positions',
                                                        'NOT_FOUND'
                                                    )}
                                                </span>
                                                <Switch
                                                    onChange={() => setAveragePositions(!averagePositions)}
                                                    checked={averagePositions}
                                                    size={'large'}
                                                />
                                            </div>
                                        </Tooltip>

                                        <div style={{ display: 'inline-block', marginTop: '10px' }}>
                                            <RadioButton
                                                style={{ marginRight: '10px' }}
                                                id="radio-near"
                                                name={'near'}
                                                value={'near'}
                                                className="primary"
                                                checked={groupingType === 'near'}
                                                onChange={handleChange}
                                            />
                                            <label htmlFor="radio-near"
                                                style={{ width: '140px', float: 'left' }}
                                            >
                                                <span>
                                                    {localizationService.toLanguageString('configuration.analysis.path_history.group_near', 'NOT_FOUND')}
                                                </span>
                                            </label>

                                            <NumericTextBox
                                                style={{ marginLeft: '10px' }}
                                                title={localizationService.toLanguageString('configuration.analysis.path_history.group_near_tooltip', 'NOT_FOUND')}
                                                defaultValue={0}
                                                width={80}
                                                min={0}
                                                max={10}
                                                size={'small'}
                                                onChange={(e) => handleValueChange('near', e.value ?? 0)}
                                                disabled={groupingType !== 'near'}
                                            />
                                        </div>

                                        <div style={{ display: 'inline-block', marginTop: '10px' }}>
                                            <RadioButton
                                                style={{ marginRight: '10px' }}
                                                id="radio-each"
                                                name='each'
                                                value='each'
                                                checked={groupingType === 'each'}
                                                onChange={handleChange}
                                            />
                                            <label htmlFor="radio-each"
                                                style={{ width: '140px', float: 'left' }}
                                            >
                                                <span>
                                                    {localizationService.toLanguageString('configuration.analysis.path_history.group_each', 'NOT_FOUND')}
                                                </span>
                                            </label>

                                            <NumericTextBox
                                                style={{ marginLeft: '10px' }}
                                                title={localizationService.toLanguageString('configuration.analysis.path_history.group_each_tooltip', 'NOT_FOUND')}
                                                defaultValue={0}
                                                width={80}
                                                min={0}
                                                max={10}
                                                size={'small'}
                                                onChange={(e) => handleValueChange('each', e.value ?? 0)}
                                                disabled={groupingType !== 'each'}
                                            />
                                        </div>

                                    </div>
                                )}

                            </div>
                        )}
                    </div>

                    {/* right panel */}
                    <div
                        className="heatmap-canvas-panel"
                        style={{ ...analysisCanvasPanelStyle }}
                    >

                        {/*map*/}
                        {analyzeEnabled && !noData &&
                            <>
                                <div
                                    className="spaghetti-static-map"
                                    style={{
                                        display: `${analyzeEnabled && !mapGeoreferenced && !noData ? 'block' : 'none'}`,
                                        border: '1px solid black',
                                    }}
                                >
                                    {showTooltip && (
                                        <div
                                            ref={tooltipRef}
                                            onMouseMove={handleDeviceTooltipStaticMap}
                                            className={`maps-tooltip maps-tooltip--device${tooltipVariant === 'device-flipped' ? ' maps-tooltip--flipped' : ''}`}
                                            style={{
                                                position: 'fixed',
                                                top: tooltipPos.y,
                                                left: tooltipPos.x,
                                                pointerEvents: 'none',
                                                zIndex: 10000,
                                                transform: tooltipVariant === 'device-flipped' ? 'translate(-100%, -50%)' : 'translate(0, -50%)'
                                            }}
                                            dangerouslySetInnerHTML={{ __html: tooltipContent }}
                                        />
                                    )}
                                    <TransformWrapper
                                        initialScale={0.2}
                                        initialPositionX={0}
                                        initialPositionY={0}
                                        minScale={0.2}
                                        centerOnInit={true}
                                        centerZoomedOut={true}
                                        limitToBounds={true}
                                        smooth={true}
                                        onTransformed={(_, state) => {
                                            setTransformState(state);
                                            if (lastMouseEvent) {
                                                handleDeviceTooltipStaticMap(lastMouseEvent);
                                            }
                                        }}
                                    >
                                        {({ zoomIn, zoomOut, resetTransform, centerView, zoomToElement, ...rest }) => (
                                            <>
                                                <div>
                                                    <Controls />

                                                    {
                                                        analyzeEnabled && !noData && selectedDevices.length > 0 &&
                                                        <div
                                                            style={{
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                gap: '8px',
                                                                marginLeft: '150px',
                                                                marginTop: '-5px',
                                                                alignItems: 'flex-start'
                                                            }}
                                                        >
                                                            {selectedDevices.map((deviceMac: string) => {
                                                                const isExpanded = Boolean(showInfoDevice[deviceMac]);
                                                                const isHidden = Boolean(hiddenDevices[deviceMac]);
                                                                const deviceLabel = deviceNameByMac[deviceMac] || deviceMac;
                                                                const signalsCount = signalsByDevice[deviceMac] ?? 0;
                                                                const deviceDistance = distanceByDevice[deviceMac] ?? distance;
                                                                const deviceChartData = accuracyChartDataByDevice[deviceMac] ?? getEmptyAccuracyChartData();

                                                                return (
                                                                    <Card
                                                                        key={`device-card-${deviceMac}`}
                                                                        style={{
                                                                            width: '250px',
                                                                            borderRadius: '0px',
                                                                            border: 'none',
                                                                            zIndex: '1',
                                                                            opacity: isHidden ? 0.72 : 1
                                                                        }}
                                                                    >
                                                                        <CardHeader className="expand-info-device-chart">
                                                                            <div style={{ display: "flex", alignItems: "center", width: "100%" }}>
                                                                                <Button
                                                                                    onClick={() => toggleDeviceVisibility(deviceMac)}
                                                                                    themeColor={isHidden ? "base" : "primary"}
                                                                                    style={{
                                                                                        height: '32px',
                                                                                        borderRadius: '0px',
                                                                                        zIndex: '1',
                                                                                        flex: 1,
                                                                                        justifyContent: 'space-between'
                                                                                    }}
                                                                                >
                                                                                    <span style={{ zIndex: '1' }}>{deviceLabel}</span>
                                                                                    <SvgIcon icon={isHidden ? eyeSlashIcon : eyeIcon} />
                                                                                </Button>
                                                                                <Button
                                                                                    onClick={() => setShowInfoDevice((prev) => ({
                                                                                        ...prev,
                                                                                        [deviceMac]: !prev[deviceMac]
                                                                                    }))}
                                                                                    style={{
                                                                                        height: '32px',
                                                                                        borderRadius: '0px',
                                                                                        minWidth: '36px',
                                                                                        marginLeft: '4px',
                                                                                        zIndex: '1',
                                                                                    }}
                                                                                >
                                                                                    <span
                                                                                        className="k-icon k-i-arrow-chevron-down"
                                                                                        style={{
                                                                                            display: "inline-block",
                                                                                            transition: "transform 0.2s",
                                                                                            transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                                                                                            zIndex: '1',
                                                                                        }}
                                                                                    >
                                                                                    </span>
                                                                                </Button>
                                                                            </div>
                                                                        </CardHeader>
                                                                        <div
                                                                            style={{
                                                                                position: "relative",
                                                                                maxWidth: isExpanded ? "250px" : "0px",
                                                                                maxHeight: isExpanded ? "300px" : "0px",
                                                                                border: isExpanded ? "2px solid #198692" : "none",
                                                                                overflow: "hidden",
                                                                                transition: "max-height 0.25s ease",
                                                                                zIndex: '1',
                                                                            }}
                                                                        >
                                                                            <CardBody style={{ position: "relative", zIndex: '1', backgroundColor: "white", paddingTop: "4px", paddingBottom: "4px" }}>
                                                                                <div style={{ display: "flex", flexDirection: "column", height: '250px' }}>

                                                                                    <div>
                                                                                        <strong>{localizationService.toLanguageString('azure.signals', 'NOT_FOUND')}:</strong>
                                                                                        &nbsp;{signalsCount}
                                                                                    </div>

                                                                                    <div>
                                                                                        <strong>{localizationService.toLanguageString('configuration.analysis.path_history.distance', 'NOT_FOUND')}:</strong>
                                                                                        &nbsp;{deviceDistance}
                                                                                    </div>

                                                                                    <div>
                                                                                        <strong>{localizationService.toLanguageString('configuration.analysis.path_history.first', 'NOT FOUND')}:</strong>
                                                                                        &nbsp;{spaghettiProgressiveFrom || sliderRangeLabels.from}
                                                                                    </div>

                                                                                    <div>
                                                                                        <strong>{localizationService.toLanguageString('configuration.analysis.path_history.last', 'NOT FOUND')}:</strong>
                                                                                        &nbsp;{toDateLabel || spaghettiProgressiveTo || sliderRangeLabels.to}
                                                                                    </div>

                                                                                    <AccuracyChart data={deviceChartData} localizationService={localizationService} />
                                                                                </div>
                                                                            </CardBody>
                                                                        </div>
                                                                    </Card>
                                                                );
                                                            })}
                                                        </div>
                                                    }

                                                </div>

                                                <TransformComponent
                                                    wrapperStyle={{ width: "100%", height: "100%" }}
                                                    contentStyle={{ width: mapImageWidth, height: mapImageHeight }}
                                                >
                                                    <div
                                                        ref={transformContentRef}
                                                        style={{
                                                            position: "relative",
                                                            width: mapImageWidth,
                                                            height: mapImageHeight
                                                        }}
                                                        onMouseMove={handleDeviceTooltipStaticMap}
                                                    >
                                                        <canvas ref={mapCanvasRef} style={{ position: "absolute", top: 0, left: 0 }} />
                                                        <canvas ref={zoneCanvasRef} style={{ position: "absolute", top: 0, left: 0 }} />
                                                        <canvas ref={lineCanvasRef} style={{ position: "absolute", top: 0, left: 0 }} />
                                                        <canvas ref={markerCanvasRef} style={{ position: "absolute", top: 0, left: 0 }} />
                                                    </div>
                                                </TransformComponent>

                                            </>
                                        )}
                                    </TransformWrapper>
                                </div>

                                <div
                                    ref={mapRef}
                                    style={{
                                        height: '100%',
                                        display: `${analyzeEnabled && mapGeoreferenced && !noData ? 'block' : 'none'}`
                                    }}
                                >
                                    {listMaps.length != 0 && mapGeoreferenced &&
                                        <AzureMapSpaghettiChart
                                            map={selectedMap}
                                            zones={showZoneMapGeo ?? []}
                                            coordinatesList={visibleCoordinates}
                                            isAdvancedDate={checked}
                                            formattedIntervalDate={formattedIntervalDate}
                                            fromDate={dateTimeState.fromHour.toLocaleString()}
                                            toDate={dateTimeState.toHour.toLocaleString()}
                                            countWakeups={countWakeups}
                                            showColor={showColor}
                                            showSlider={false}
                                            externalSliderStartPercent={sliderRangePercent.start}
                                            analysisKey={analysisRunId}
                                            externalSliderPercent={sliderRangePercent.end}
                                            onSliderDateChange={handleGeoSliderDateChange}
                                            showAllPoints={showAllPoints}
                                            selectedDevices={visibleSelectedDevices}
                                            deviceNamesByMac={deviceNameByMac}
                                            distanceByDevice={distanceByDevice}
                                            signalsGrouping={signalsGrouping}
                                        />
                                    }
                                </div>
                                {/*}*/}
                            </>
                        }

                    </div>
                </div >
            }
        </>
    )
}
